import { useEffect, useMemo, useState, type ChangeEvent, type MouseEvent } from "react";
import { Info } from "lucide-react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { useApi } from "../api/useApi";
import {
  DEFAULT_SIMULATION_COUNT,
  planFactors,
  projectMarginPlan,
  type MarginPlanInputs,
  type MarginProjectionYear,
} from "../lib/marginProjection";
import { useMarginSimulation } from "../lib/useMarginSimulation";
import {
  loadMarginPlans,
  normalizeMarginPlanInputs,
  removeLocalSavedMarginPlans,
  storeMarginPlanDraft,
  storeSelectedMarginPlan,
  type SavedMarginPlan,
} from "../lib/marginPlanStorage";
import {
  buildMarginPlanExport,
  downloadMarginPlanExcel,
  downloadMarginPlanJson,
  type FundingSummaryExport,
} from "../lib/marginPlanExport";
import {
  buildMarginPlanConfigTransfer,
  downloadMarginPlanConfig,
  parseMarginPlanConfigTransfer,
} from "../lib/marginPlanConfigTransfer";
import {
  BASE_MAINTENANCE_PERCENT,
  portfolioMaintenance,
} from "../lib/marginMaintenance";
import type {
  BrokerageAccountSummary,
  MarginRateEstimate,
  PortfolioAssumptions,
} from "../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const DEFAULT_INPUTS: MarginPlanInputs = {
  currentAge: 43,
  retirementAge: 43,
  retirementAccessAge: 59.5,
  endAge: 90,
  startingPortfolio: 3_178_734,
  retirementAssets: 0,
  externalAssets: 0,
  externalAssetGrowthPercent: 3,
  initialMargin: 0,
  annualSpending: 120_000,
  spendingDollarBasis: "retirement_start",
  spendingMode: "fixed",
  spendingPercent: 4,
  spendingBorrowPercent: 100,
  dividendYieldPercent: 1.5,
  dividendTaxRatePercent: 23.8,
  dividendUse: "spending",
  capitalGainsTaxEnabled: false,
  startingTaxableCostBasis: 3_178_734,
  capitalGainsTaxRatePercent: 23.8,
  flexibleSpendingEnabled: false,
  downMarketTriggerPercent: 10,
  spendingCutWithdrawalRatePercent: 3,
  downMarketSpendingReductionPercent: 10,
  minimumSpendingPercent: 75,
  monthlyContribution: 0,
  contributionMode: "fixed",
  contributionPercent: 5,
  contributionEndAge: 43,
  contributionStrategy: "invest",
  cashFlowMode: "manual_contributions",
  annualWorkingIncome: 0,
  incomeGrowthPercent: 3,
  annualWorkingExpenses: 0,
  workingExpenseInflationPercent: 2.5,
  retirementIncomeMode: "none",
  annualCoastIncome: 0,
  coastIncomeGrowthPercent: 2.5,
  coastIncomeEndAge: 67,
  monthlySocialSecurityBenefit: 0,
  socialSecurityStartAge: 67,
  socialSecurityColaPercent: 2.5,
  monthlyPensionBenefit: 0,
  pensionStartAge: 65,
  pensionColaPercent: 0,
  expectedReturnPercent: 8,
  volatilityPercent: 18,
  marginRatePercent: 7,
  marginRateMode: "fixed",
  marginRateMinPercent: 4,
  marginRateMaxPercent: 12,
  inflationPercent: 2.5,
  maxLtvPercent: 100 - BASE_MAINTENANCE_PERCENT,
  maintenanceShockEnabled: false,
  maintenanceShockTimingMode: "fixed",
  maintenanceShockAge: 50,
  maintenanceShockLatestAge: 70,
  maintenanceShockDurationYears: 0,
  stressedMaxLtvPercent: 40,
  pauseBorrowingLtvPercent: 50,
  deleveragingLtvPercent: 65,
  targetLtvPercent: 50,
};

const money = (value: number, compact = false) => new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: compact ? 0 : 2,
  notation: compact ? "compact" : "standard",
}).format(value);

const deltaMoney = (value: number) => `${value > 0 ? "+" : ""}${money(value, true)}`;

function fundingSummary(rows: MarginProjectionYear[]) {
  const final = rows.at(-1)!;
  return {
    final,
    interest: rows.reduce((sum, row) => sum + row.marginInterest, 0),
    assetSales: rows.reduce(
      (sum, row) => sum + row.taxableAssetsSold + row.retirementAssetsSold,
      0,
    ),
    dividendsForSpending: rows.reduce(
      (sum, row) => sum + row.dividendUsedForSpending,
      0,
    ),
    dividendTaxes: rows.reduce((sum, row) => sum + row.dividendTaxes, 0),
    capitalGains: rows.reduce((sum, row) => sum + row.realizedCapitalGains, 0),
    capitalGainsTaxes: rows.reduce((sum, row) => sum + row.capitalGainsTaxes, 0),
    unfundedSpending: rows.reduce((sum, row) => sum + row.unfundedSpending, 0),
    minimumCushion: Math.min(...rows.map((row) => row.maintenanceCushion)),
  };
}

// Planner inputs intentionally stop at one decimal so imported estimates and weighted
// portfolio values do not expose meaningless floating-point precision in editable fields.
const oneDecimal = (value: number) => Math.round((value + Number.EPSILON) * 10) / 10;
const CONTROL_GRID_CLASS = "grid grid-cols-2 items-end gap-4 md:grid-cols-4";

function FieldLabel({
  htmlFor,
  children,
  help,
}: {
  htmlFor: string;
  children: string;
  help?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{children}</Label>
      {help && (
        <TooltipPrimitive.Provider delayDuration={150}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <button
                type="button"
                className="inline-flex cursor-help text-muted-foreground"
                aria-label={`${children} help: ${help}`}
              >
                <Info className="size-3.5" aria-hidden="true" />
              </button>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side="bottom"
                align="start"
                sideOffset={4}
                collisionPadding={12}
                className="z-50 w-72 max-w-[calc(100vw-1.5rem)] rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal leading-relaxed text-popover-foreground shadow-md"
              >
                {help}
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      )}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  max,
  suffix,
  help,
  disabled = false,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  help?: string;
  disabled?: boolean;
}) {
  const normalizedValue = oneDecimal(value);
  const [draft, setDraft] = useState(String(normalizedValue));

  useEffect(() => {
    setDraft(String(normalizedValue));
  }, [normalizedValue]);

  const commitDraft = (raw: string) => {
    setDraft(raw);
    if (raw.trim() === "") return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    // Preserve intermediate input such as the first "5" while typing "55"
    // into a field whose minimum is the current age. Committing/clamping that
    // partial value in the parent would reset the draft before typing finishes.
    if (min != null && parsed < min) return;
    if (max != null && parsed > max) return;
    onChange(oneDecimal(parsed));
  };

  return (
    <div className={`min-w-0 space-y-1.5 ${disabled ? "opacity-55" : ""}`}>
      <FieldLabel htmlFor={id} help={help}>{label}</FieldLabel>
      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={draft}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => commitDraft(event.target.value)}
          onBlur={() => {
            const parsed = Number(draft);
            if (draft.trim() === ""
              || !Number.isFinite(parsed)
              || (min != null && parsed < min)
              || (max != null && parsed > max)) {
              setDraft(String(normalizedValue));
            }
          }}
          className={suffix ? "pr-10" : undefined}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ProjectionChart({ rows, inputs }: { rows: MarginProjectionYear[]; inputs: MarginPlanInputs }) {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);
  const [balanceScale, setBalanceScale] = useState<"log" | "linear">("log");
  const [chartZoom, setChartZoom] = useState(1);
  const width = 800;
  const height = 260;
  const pad = 18;
  const plotTop = 24;
  const plotBottom = height - pad;
  const points = [
    {
      year: 0,
      portfolio: inputs.startingPortfolio + inputs.retirementAssets,
      netWorth: inputs.startingPortfolio + inputs.retirementAssets + inputs.externalAssets - inputs.initialMargin,
      margin: inputs.initialMargin,
      marginLimit: inputs.startingPortfolio * inputs.maxLtvPercent / 100,
      effectiveMaxLtvPercent: inputs.maxLtvPercent,
      spending: 0,
      spendingFunded: 0,
      unfundedSpending: 0,
      spendingBorrowed: 0,
      spendingFromPortfolio: 0,
      spendingReduction: 0,
      plannedWithdrawalRatePercent: 0,
      dividendUsedForSpending: 0,
      marginRepaidFromDividends: 0,
      accessibleDividendIncome: 0,
      inaccessibleRetirementDividendIncome: 0,
      dividendReinvested: 0,
      netMarginChange: 0,
      earnedIncome: 0,
      socialSecurityIncome: 0,
      pensionIncome: 0,
      dividendIncome: 0,
      dividendTaxes: 0,
      realizedCapitalGains: 0,
      capitalGainsTaxes: 0,
      livingExpenses: 0,
    },
    ...rows.map((row, index) => ({
      year: row.year,
      portfolio: row.portfolioEnd,
      netWorth: row.netWorth,
      margin: row.marginEnd,
      marginLimit: row.taxablePortfolioEnd * row.effectiveMaxLtvPercent / 100,
      effectiveMaxLtvPercent: row.effectiveMaxLtvPercent,
      spending: row.spendingTotal,
      spendingFunded: row.spendingFunded,
      unfundedSpending: row.unfundedSpending,
      spendingBorrowed: row.spendingBorrowed,
      spendingFromPortfolio: row.spendingFromPortfolio,
      spendingReduction: row.spendingReduction,
      plannedWithdrawalRatePercent: row.plannedWithdrawalRatePercent,
      dividendUsedForSpending: row.dividendUsedForSpending,
      marginRepaidFromDividends: row.marginRepaidFromDividends,
      accessibleDividendIncome: row.accessibleDividendIncome,
      inaccessibleRetirementDividendIncome: row.inaccessibleRetirementDividendIncome,
      dividendReinvested: row.dividendReinvested,
      netMarginChange: row.marginEnd - (index === 0 ? inputs.initialMargin : rows[index - 1].marginEnd),
      earnedIncome: row.earnedIncome,
      socialSecurityIncome: row.socialSecurityIncome,
      pensionIncome: row.pensionIncome,
      dividendIncome: row.dividendIncome,
      dividendTaxes: row.dividendTaxes,
      realizedCapitalGains: row.realizedCapitalGains,
      capitalGainsTaxes: row.capitalGainsTaxes,
      livingExpenses: row.livingExpenses,
    })),
  ];
  type BalanceKey = "portfolio" | "netWorth" | "margin" | "marginLimit";
  type SpendingKey = "spending" | "spendingBorrowed" | "spendingFromPortfolio"
    | "spendingReduction" | "earnedIncome" | "socialSecurityIncome" | "pensionIncome"
    | "dividendIncome" | "livingExpenses";
  type ChartKey = BalanceKey | SpendingKey;
  const balanceValues = points.flatMap((point) => [
    point.portfolio,
    point.netWorth,
    point.margin,
    point.marginLimit,
  ]);
  const minBalance = Math.min(0, ...balanceValues);
  const maxBalance = Math.max(1, ...balanceValues);
  const maxSpending = Math.max(
    1,
    ...points.flatMap((point) => [
      point.spending,
      point.spendingBorrowed,
      point.spendingFromPortfolio,
      point.spendingReduction,
      point.earnedIncome,
      point.dividendIncome,
      point.livingExpenses,
    ]),
  );
  const x = (year: number) => pad + (year / Math.max(1, rows.length)) * (width - pad * 2);
  const scaleY = (value: number, minimum: number, maximum: number) => (
    plotBottom - ((value - minimum) / Math.max(1, maximum - minimum)) * (plotBottom - plotTop)
  );
  // Symmetric log keeps zero and negative net worth representable while making
  // decades of compounding legible. The threshold preserves detail near zero.
  const logThreshold = Math.max(1, Math.max(Math.abs(minBalance), maxBalance) / 1_000);
  const symlog = (value: number) => Math.sign(value) * Math.log1p(Math.abs(value) / logThreshold);
  const transformedMin = symlog(minBalance);
  const transformedMax = symlog(maxBalance);
  const balanceY = (value: number) => balanceScale === "log"
    ? scaleY(symlog(value), transformedMin, transformedMax)
    : scaleY(value, minBalance, maxBalance);
  const cashFlowY = (value: number) => scaleY(Math.max(0, value), 0, maxSpending);
  const path = (key: ChartKey, y: (value: number) => number) => points
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(point.year).toFixed(1)},${y(point[key]).toFixed(1)}`)
    .join(" ");
  const balancePath = (key: BalanceKey) => path(key, balanceY);
  const spendingPath = (key: SpendingKey) => path(key, cashFlowY);
  const retirementYear = Math.min(
    rows.length,
    Math.max(0, inputs.retirementAge - inputs.currentAge),
  );
  const activeYear = hoveredYear ?? selectedYear;
  const selectedPoint = activeYear == null ? null : points[activeYear] ?? null;
  const yearFromEvent = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const svgX = ((event.clientX - rect.left) / rect.width) * width;
    const plotFraction = (svgX - pad) / (width - pad * 2);
    return Math.round(Math.max(0, Math.min(1, plotFraction)) * rows.length);
  };
  const onChartClick = (event: MouseEvent<SVGSVGElement>) => {
    const year = yearFromEvent(event);
    if (year != null) setSelectedYear(year);
  };
  const onChartHover = (event: MouseEvent<SVGSVGElement>) => {
    const year = yearFromEvent(event);
    if (year != null) setHoveredYear(year);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        <span className="flex items-center gap-2"><span className="size-2 rounded-full bg-primary" />Portfolio</span>
        <span className="flex items-center gap-2"><span className="size-2 rounded-full bg-success" />Net worth</span>
        <span className="flex items-center gap-2"><span className="size-2 rounded-full bg-destructive" />Margin debt</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dashed border-destructive" />Maintenance debt limit</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 bg-warning" />Portfolio draw</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dashed border-destructive" />Gross margin borrowing</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dotted border-muted-foreground" />Portfolio-funded spending</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dashed border-primary" />Flexible spending cut</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 bg-success" />Income</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dashed border-success" />Dividends</span>
        <span className="flex items-center gap-2"><span className="h-px w-4 border-t border-dashed border-warning" />Living expenses</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1 md:hidden" aria-label="Timeline zoom controls">
          <span className="mr-1">Timeline zoom</span>
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-label="Zoom chart out"
            disabled={chartZoom <= 1}
            onClick={() => setChartZoom((current) => Math.max(1, current - 0.5))}
          >
            −
          </Button>
          <span className="w-8 text-center tabular-nums" aria-live="polite">
            {Math.round(chartZoom * 100)}%
          </span>
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-label="Zoom chart in"
            disabled={chartZoom >= 3}
            onClick={() => setChartZoom((current) => Math.min(3, current + 0.5))}
          >
            +
          </Button>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <span className="mr-1">Balance scale</span>
          <Button
            type="button"
            size="xs"
            variant={balanceScale === "linear" ? "secondary" : "ghost"}
            aria-pressed={balanceScale === "linear"}
            onClick={() => setBalanceScale("linear")}
          >
            Linear
          </Button>
          <Button
            type="button"
            size="xs"
            variant={balanceScale === "log" ? "secondary" : "ghost"}
            aria-pressed={balanceScale === "log"}
            onClick={() => setBalanceScale("log")}
          >
            Log
          </Button>
        </div>
      </div>
      <div
        className="overflow-x-auto overscroll-x-contain pb-1"
        data-testid="planner-chart-scroll"
      >
        <div
          className="md:!w-full"
          style={{ width: `${chartZoom * 100}%` }}
          data-testid="planner-chart-scroll-content"
        >
          <svg
            className="h-auto w-full cursor-crosshair"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Projected portfolio, net worth, margin debt, maintenance limit, income, annual expenses, and flexible spending cuts by year. Hover or click to inspect a year."
            onClick={onChartClick}
            onMouseMove={onChartHover}
            onMouseLeave={() => setHoveredYear(null)}
          >
        <text x={pad} y="12" fill="var(--muted-foreground)" fontSize="10">Balances up to {money(maxBalance, true)} · {balanceScale === "log" ? "log" : "linear"}</text>
        <text x={width - pad} y="12" fill="var(--muted-foreground)" fontSize="10" textAnchor="end">Annual cash flow up to {money(maxSpending, true)}</text>
        <line x1={pad} y1={plotBottom} x2={width - pad} y2={plotBottom} stroke="var(--border)" />
        <line
          x1={x(retirementYear)}
          y1={plotTop}
          x2={x(retirementYear)}
          y2={plotBottom}
          stroke="var(--muted-foreground)"
          strokeDasharray="3 5"
          opacity="0.55"
        />
        <text
          x={x(retirementYear) + (retirementYear > rows.length * 0.78 ? -5 : 5)}
          y={plotTop + 11}
          fill="var(--muted-foreground)"
          fontSize="10"
          textAnchor={retirementYear > rows.length * 0.78 ? "end" : "start"}
        >
          Retirement · age {inputs.retirementAge}
        </text>
        {selectedPoint && (
          <line
            data-testid="planner-chart-crosshair"
            x1={x(selectedPoint.year)}
            y1={plotTop}
            x2={x(selectedPoint.year)}
            y2={plotBottom}
            stroke="var(--muted-foreground)"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.75"
          />
        )}
        <path data-testid="planner-chart-margin-limit" d={balancePath("marginLimit")} fill="none" stroke="var(--destructive)" strokeWidth="2" strokeDasharray="10 6" opacity="0.45" />
        <path data-testid="planner-chart-portfolio" d={balancePath("portfolio")} fill="none" stroke="var(--primary)" strokeWidth="4" />
        <path data-testid="planner-chart-net-worth" d={balancePath("netWorth")} fill="none" stroke="var(--success)" strokeWidth="3" />
        <path data-testid="planner-chart-margin" d={balancePath("margin")} fill="none" stroke="var(--destructive)" strokeWidth="3" />
        <path data-testid="planner-chart-spending-total" d={spendingPath("spending")} fill="none" stroke="var(--warning)" strokeWidth="3" />
        <path data-testid="planner-chart-spending-borrowed" d={spendingPath("spendingBorrowed")} fill="none" stroke="var(--destructive)" strokeWidth="2" strokeDasharray="8 5" />
        <path data-testid="planner-chart-spending-portfolio" d={spendingPath("spendingFromPortfolio")} fill="none" stroke="var(--muted-foreground)" strokeWidth="2" strokeDasharray="2 5" />
        <path data-testid="planner-chart-spending-reduction" d={spendingPath("spendingReduction")} fill="none" stroke="var(--primary)" strokeWidth="2" strokeDasharray="5 5" />
        <path data-testid="planner-chart-income" d={spendingPath("earnedIncome")} fill="none" stroke="var(--success)" strokeWidth="2" />
        <path data-testid="planner-chart-dividends" d={spendingPath("dividendIncome")} fill="none" stroke="var(--success)" strokeWidth="2" strokeDasharray="3 5" opacity="0.8" />
        <path data-testid="planner-chart-living-expenses" d={spendingPath("livingExpenses")} fill="none" stroke="var(--warning)" strokeWidth="2" strokeDasharray="8 5" />
        {selectedPoint && (
          <>
            <circle cx={x(selectedPoint.year)} cy={balanceY(selectedPoint.marginLimit)} r="3" fill="var(--destructive)" opacity="0.65" />
            <circle cx={x(selectedPoint.year)} cy={balanceY(selectedPoint.portfolio)} r="4" fill="var(--primary)" />
            <circle cx={x(selectedPoint.year)} cy={balanceY(selectedPoint.netWorth)} r="4" fill="var(--success)" />
            <circle cx={x(selectedPoint.year)} cy={balanceY(selectedPoint.margin)} r="4" fill="var(--destructive)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.spending)} r="3" fill="var(--warning)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.spendingBorrowed)} r="3" fill="var(--destructive)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.spendingFromPortfolio)} r="3" fill="var(--muted-foreground)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.spendingReduction)} r="3" fill="var(--primary)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.earnedIncome)} r="3" fill="var(--success)" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.dividendIncome)} r="3" fill="var(--success)" opacity="0.8" />
            <circle cx={x(selectedPoint.year)} cy={cashFlowY(selectedPoint.livingExpenses)} r="3" fill="var(--warning)" />
          </>
        )}
          </svg>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>Age {inputs.currentAge}</span>
            <span>Age {inputs.endAge}</span>
          </div>
        </div>
      </div>
      {selectedPoint && (
        <div
          className="rounded-lg border border-border/60 p-3 text-xs"
          data-testid="planner-chart-selection"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="font-medium">Age {inputs.currentAge + selectedPoint.year}</p>
              <p className="text-[10px] text-muted-foreground">Plan year {selectedPoint.year}</p>
            </div>
            {selectedYear != null && (
              <button
                type="button"
                className="shrink-0 text-[10px] text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedYear(null)}
              >
                Clear selection
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {[
              ["Investments", selectedPoint.portfolio],
              ["Net worth", selectedPoint.netWorth],
              ["Margin debt", selectedPoint.margin],
              ["Cash needed", selectedPoint.spending],
            ].map(([label, value]) => (
              <div className="rounded-md border border-border/60 bg-muted/10 p-2.5" key={label as string}>
                <p className="text-[10px] text-muted-foreground">{label}</p>
                <p className="mt-0.5 font-semibold tabular-nums">{money(value as number)}</p>
                {label === "Margin debt" && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {selectedPoint.marginLimit > 0 && selectedPoint.effectiveMaxLtvPercent > 0
                      ? `${(selectedPoint.margin / (selectedPoint.marginLimit / selectedPoint.effectiveMaxLtvPercent)).toFixed(1)}% LTV`
                      : "No taxable collateral"}
                  </p>
                )}
                {label === "Cash needed" && (
                  <>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      {selectedPoint.plannedWithdrawalRatePercent.toFixed(2)}% pre-cut withdrawal rate
                    </p>
                    <p className={`text-[10px] ${selectedPoint.unfundedSpending > 0.005 ? "text-destructive" : "text-success"}`}>
                      {selectedPoint.unfundedSpending > 0.005
                        ? `${money(selectedPoint.unfundedSpending)} shortfall`
                        : "Fully funded"}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>

          <section className="mt-3 rounded-md border border-border/60 p-3" aria-labelledby="spending-funding-heading">
            <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2">
              <div>
                <h3 id="spending-funding-heading" className="font-medium">How spending was covered</h3>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Living expenses minus income leaves the cash needed from investments.
                </p>
              </div>
              <span className="shrink-0 font-semibold tabular-nums">{money(selectedPoint.spendingFunded)}</span>
            </div>
            <dl className="mt-2 grid gap-x-6 gap-y-1.5 tabular-nums sm:grid-cols-2">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Living expenses</dt>
                <dd className="font-medium">{money(selectedPoint.livingExpenses)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">Income offset</dt>
                <dd className="font-medium">−{money(Math.min(selectedPoint.livingExpenses, selectedPoint.earnedIncome))}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-border/40 pt-1.5 sm:col-span-2">
                <dt className="font-medium">Cash needed from investments</dt>
                <dd className="font-semibold">{money(selectedPoint.spending)}</dd>
              </div>
              {[
                ["Dividends", selectedPoint.dividendUsedForSpending],
                ["Asset sales after tax", selectedPoint.spendingFromPortfolio],
                ["Margin borrowing", selectedPoint.spendingBorrowed],
                ["Unfunded", selectedPoint.unfundedSpending],
              ].filter(([, value]) => (value as number) > 0.005).map(([label, value]) => (
                <div className="flex items-baseline justify-between gap-3" key={label as string}>
                  <dt className={label === "Unfunded" ? "text-destructive" : "text-muted-foreground"}>{label}</dt>
                  <dd className={`font-medium ${label === "Unfunded" ? "text-destructive" : ""}`}>{money(value as number)}</dd>
                </div>
              ))}
              {selectedPoint.spending <= 0.005 && (
                <div className="text-muted-foreground sm:col-span-2">No investment funding was needed this year.</div>
              )}
            </dl>
            {inputs.dividendUse === "pay_margin"
              && selectedPoint.marginRepaidFromDividends > 0.005
              && selectedPoint.spendingBorrowed > 0.005 && (
              <p className="mt-2 rounded-md bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground">
                Your dividend priority pays {money(selectedPoint.marginRepaidFromDividends)} toward existing margin first, so spending is funded separately. Choosing “Fund spending first” would net those dividends against new borrowing before using any remainder to reduce margin.
              </p>
            )}
          </section>

          <details className="mt-3 rounded-md border border-border/60">
            <summary className="cursor-pointer select-none px-3 py-2.5 font-medium hover:bg-muted/20">
              Show calculation details
            </summary>
            <div className="grid gap-4 border-t border-border/60 p-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  label: "Income details",
                  values: [
                    ["Income total", selectedPoint.earnedIncome],
                    ["Work / coast", Math.max(0, selectedPoint.earnedIncome - selectedPoint.socialSecurityIncome - selectedPoint.pensionIncome)],
                    ["Social Security", selectedPoint.socialSecurityIncome],
                    ["Pension", selectedPoint.pensionIncome],
                    ["Flexible spending cut", selectedPoint.spendingReduction],
                  ],
                },
                {
                  label: "Dividend & tax details",
                  values: [
                    ["Dividends (total)", selectedPoint.dividendIncome],
                    ["Dividend taxes", selectedPoint.dividendTaxes],
                    ["Accessible dividends after tax", selectedPoint.accessibleDividendIncome],
                    ["Retirement dividends retained", selectedPoint.inaccessibleRetirementDividendIncome],
                    ["Accessible dividends reinvested", selectedPoint.dividendReinvested],
                    ["Realized capital gains", selectedPoint.realizedCapitalGains],
                    ["Capital-gains taxes", selectedPoint.capitalGainsTaxes],
                  ],
                },
                {
                  label: "Margin details",
                  values: [
                    ["Debt limit", selectedPoint.marginLimit],
                    ["Gross margin borrowing", selectedPoint.spendingBorrowed],
                    ["Margin repaid from dividends", selectedPoint.marginRepaidFromDividends],
                    ["Net margin debt change", selectedPoint.netMarginChange],
                  ],
                },
              ].map((group) => (
                <section key={group.label}>
                  <h3 className="mb-1.5 border-b border-border/60 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h3>
                  <dl className="space-y-1 tabular-nums">
                    {group.values.map(([label, value]) => (
                      <div className="flex min-w-0 items-baseline justify-between gap-3" key={label}>
                        <dt className="truncate text-muted-foreground">{label}</dt>
                        <dd className="shrink-0 font-medium">{money(value as number)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          </details>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        Balances use the left scale. Cash flow uses the right. Hover to preview; click to pin.
      </p>
    </div>
  );
}

function milestoneRows(
  rows: MarginProjectionYear[],
  retirementAge: number,
): MarginProjectionYear[] {
  const firstRetirementYear = rows.find(
    (row) => row.age - 1 >= retirementAge,
  )?.year;
  return rows.filter((row) => (
    row.year <= 10
    || row.year % 5 === 0
    || row.year === firstRetirementYear
    || row.year === (firstRetirementYear ?? 0) - 1
    || row === rows.at(-1)
  ));
}

function ProjectionMetricCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <TableCell className="block p-0 text-left tabular-nums md:table-cell md:p-2 md:text-right">
      <span className="block text-xs text-muted-foreground md:hidden">{label}</span>
      {children}
    </TableCell>
  );
}

export function MarginPlanner() {
  const api = useApi();
  const initialPlans = useMemo(() => loadMarginPlans(DEFAULT_INPUTS), []);
  const [inputs, setInputs] = useState(initialPlans.draft);
  const [savedPlans, setSavedPlans] = useState<SavedMarginPlan[]>(initialPlans.savedPlans);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlans.selectedPlanId ?? "");
  const [planName, setPlanName] = useState(
    initialPlans.savedPlans.find((plan) => plan.id === initialPlans.selectedPlanId)?.name ?? "",
  );
  const [saveStatus, setSaveStatus] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [exportingExcel, setExportingExcel] = useState(false);
  const [linked, setLinked] = useState<BrokerageAccountSummary | null>(null);
  const [portfolioAssumptions, setPortfolioAssumptions] = useState<PortfolioAssumptions | null>(null);
  const [marginRateEstimate, setMarginRateEstimate] = useState<MarginRateEstimate | null>(null);
  const [portfolioStatus, setPortfolioStatus] = useState("");
  const [marginRateStatus, setMarginRateStatus] = useState("");
  const [maintenanceStatus, setMaintenanceStatus] = useState("");
  const [analyzingPortfolio, setAnalyzingPortfolio] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [planMutationPending, setPlanMutationPending] = useState(false);

  useEffect(() => {
    let active = true;
    void api.getBrokerageAccounts()
      .then((summary) => {
        if (active) setLinked(summary);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    const stored = storeMarginPlanDraft(inputs);
    if (!stored) setSaveStatus("This browser could not save planner data.");
  }, [inputs]);

  useEffect(() => {
    let active = true;
    const syncPlans = async () => {
      try {
        const remotePlans = (await api.listMarginPlans()).flatMap((plan) => {
          const normalized = normalizeMarginPlanInputs(plan.inputs, DEFAULT_INPUTS);
          return normalized ? [{ ...plan, inputs: normalized }] : [];
        });
        const remoteIds = new Set(remotePlans.map((plan) => plan.id));
        const alreadyRemote = initialPlans.savedPlans.filter((plan) => remoteIds.has(plan.id));
        const pending = initialPlans.savedPlans.filter((plan) => !remoteIds.has(plan.id));
        const results = await Promise.allSettled(
          pending.map((plan) => api.putMarginPlan(plan)),
        );
        if (!active) return;

        const migrated = results.flatMap((result) => (
          result.status === "fulfilled" ? [result.value] : []
        ));
        const failed = pending.filter((_, index) => results[index].status === "rejected");
        const merged = new Map<string, SavedMarginPlan>();
        [...remotePlans, ...migrated, ...failed].forEach((plan) => merged.set(plan.id, plan));
        const availablePlans = [...merged.values()].sort((a, b) => a.name.localeCompare(b.name));
        setSavedPlans(availablePlans);
        const rememberedPlan = availablePlans.find(
          (plan) => plan.id === initialPlans.selectedPlanId,
        );
        const defaultPlan = initialPlans.selectedPlanId === null
          ? [...availablePlans].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
          : undefined;
        const planToSelect = rememberedPlan ?? defaultPlan;
        if (planToSelect) {
          setSelectedPlanId(planToSelect.id);
          setPlanName(planToSelect.name);
          setInputs(planToSelect.inputs);
          storeSelectedMarginPlan(planToSelect.id);
        } else if (initialPlans.selectedPlanId) {
          setSelectedPlanId("");
          setPlanName("");
          storeSelectedMarginPlan("");
        }

        const confirmedIds = [...alreadyRemote, ...migrated].map((plan) => plan.id);
        if (confirmedIds.length > 0) removeLocalSavedMarginPlans(confirmedIds);
        if (migrated.length > 0 && failed.length === 0) {
          setSaveStatus(
            `Moved ${migrated.length} saved configuration${migrated.length === 1 ? "" : "s"} to your account.`,
          );
        } else if (failed.length > 0) {
          setSaveStatus(
            `${failed.length} local configuration${failed.length === 1 ? "" : "s"} could not sync and remain on this device.`,
          );
        }
      } catch {
        if (active) {
          setSaveStatus(
            "Saved configurations could not sync. Local configurations remain on this device.",
          );
        }
      } finally {
        if (active) setPlansLoading(false);
      }
    };
    void syncPlans();
    return () => {
      active = false;
    };
  }, [api, initialPlans.savedPlans, initialPlans.selectedPlanId]);

  const projection = useMemo(() => projectMarginPlan(inputs), [inputs]);
  const marginFunding = useMemo(
    () => fundingSummary(projectMarginPlan({ ...inputs, spendingBorrowPercent: 100 })),
    [inputs],
  );
  const sellFunding = useMemo(
    () => fundingSummary(projectMarginPlan({
      ...inputs,
      spendingBorrowPercent: 0,
      dividendUse: "spending",
    })),
    [inputs],
  );
  const { simulation, simulatedInputs, isPending: simulationPending } = useMarginSimulation(inputs);
  const simulationInputs = simulatedInputs ?? inputs;
  const factors = useMemo(() => planFactors(inputs, projection), [inputs, projection]);
  const final = projection.at(-1)!;
  const maintenanceRequirementPercent = Math.max(0, 100 - inputs.maxLtvPercent);
  const effectiveStressedMaxLtv = Math.min(
    inputs.maxLtvPercent,
    inputs.stressedMaxLtvPercent,
  );
  const stressedDeleveragingBuffer = effectiveStressedMaxLtv
    - inputs.deleveragingLtvPercent;
  const effectiveStressedDeleveragingLtv = Math.min(
    inputs.deleveragingLtvPercent,
    effectiveStressedMaxLtv,
  );
  const effectiveStressedTargetLtv = Math.min(
    inputs.targetLtvPercent,
    effectiveStressedDeleveragingLtv,
  );
  const startingContributionPercent = inputs.contributionMode === "portfolio_percent"
    ? inputs.contributionPercent
    : inputs.startingPortfolio > 0
      ? inputs.monthlyContribution * 12 / inputs.startingPortfolio * 100
      : 0;
  const minimumCushion = Math.min(...projection.map((row) => row.maintenanceCushion));
  const totalRetirementExpenses = projection.reduce(
    (sum, row) => sum + (row.age - 1 >= inputs.retirementAge ? row.livingExpenses : 0),
    0,
  );
  const totalEarnedIncome = projection.reduce((sum, row) => sum + row.earnedIncome, 0);
  const totalSocialSecurityIncome = projection.reduce(
    (sum, row) => sum + row.socialSecurityIncome,
    0,
  );
  const totalPensionIncome = projection.reduce((sum, row) => sum + row.pensionIncome, 0);
  const estimatedSocialSecurityAtClaim = Math.max(
    0,
    inputs.monthlySocialSecurityBenefit,
  ) * (1 + Math.max(-0.99, inputs.socialSecurityColaPercent / 100)) ** Math.max(
    0,
    inputs.socialSecurityStartAge - inputs.currentAge,
  );
  const totalMarginInterest = projection.reduce((sum, row) => sum + row.marginInterest, 0);
  const totalFlexibleSpendingReduction = projection.reduce(
    (sum, row) => sum + row.spendingReduction,
    0,
  );
  const totalDeleveragingSales = projection.reduce((sum, row) => sum + row.deleveragingSale, 0);
  const totalContributionMarginRepayment = projection.reduce(
    (sum, row) => sum + row.marginRepaidFromContributions,
    0,
  );
  const totalDividendIncome = projection.reduce((sum, row) => sum + row.dividendIncome, 0);
  const totalDividendTaxes = projection.reduce((sum, row) => sum + row.dividendTaxes, 0);
  const totalRealizedCapitalGains = projection.reduce(
    (sum, row) => sum + row.realizedCapitalGains,
    0,
  );
  const totalCapitalGainsTaxes = projection.reduce(
    (sum, row) => sum + row.capitalGainsTaxes,
    0,
  );
  const totalDividendSpending = projection.reduce(
    (sum, row) => sum + row.dividendUsedForSpending,
    0,
  );
  const totalDividendMarginRepayment = projection.reduce(
    (sum, row) => sum + row.marginRepaidFromDividends,
    0,
  );
  const totalUnfundedSpending = projection.reduce(
    (sum, row) => sum + row.unfundedSpending,
    0,
  );
  const retirementStartFixedSpending = inputs.annualSpending
    * (inputs.spendingDollarBasis === "today"
      ? (1 + inputs.inflationPercent / 100) ** Math.max(0, inputs.retirementAge - inputs.currentAge)
      : 1);
  const quality = simulation === null
    ? "Calculating"
    : simulation.successPercent >= 85
    ? "High resilience"
    : simulation.successPercent >= 65
      ? "Moderate resilience"
      : "Low resilience";
  const qualityVariant = simulation === null
    ? "secondary"
    : simulation.successPercent >= 85
    ? "default"
    : simulation.successPercent >= 65
      ? "secondary"
      : "destructive";
  const update = <K extends keyof MarginPlanInputs>(key: K, value: MarginPlanInputs[K]) => {
    setInputs((current) => ({ ...current, [key]: value }));
    setSaveStatus("");
  };
  const selectedPlan = savedPlans.find((candidate) => candidate.id === selectedPlanId);
  const selectedPlanHasUnsavedChanges = selectedPlan != null && (
    planName.trim() !== selectedPlan.name
    || JSON.stringify(inputs) !== JSON.stringify(selectedPlan.inputs)
  );
  const chooseSavedPlan = (id: string) => {
    if (id === selectedPlanId) return;
    if (selectedPlanHasUnsavedChanges && !window.confirm(
      "Discard unsaved changes and load another configuration?",
    )) return;
    setSelectedPlanId(id);
    storeSelectedMarginPlan(id);
    const plan = savedPlans.find((candidate) => candidate.id === id);
    setPlanName(plan?.name ?? "");
    if (!plan) return;
    setInputs(plan.inputs);
    setPortfolioAssumptions(null);
    setMarginRateEstimate(null);
    setPortfolioStatus("");
    setMarginRateStatus("");
    setMaintenanceStatus("");
    setSaveStatus(`Loaded “${plan.name}”.`);
  };
  const savePlan = async () => {
    const name = planName.trim();
    if (!name) {
      setSaveStatus("Enter a configuration name first.");
      document.getElementById("plan-name")?.focus();
      return;
    }
    const existingByName = savedPlans.find(
      (plan) => plan.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
    const id = selectedPlanId || existingByName?.id || crypto.randomUUID();
    const plan: SavedMarginPlan = {
      id,
      name,
      inputs,
      updatedAt: new Date().toISOString(),
    };
    setPlanMutationPending(true);
    try {
      const savedPlan = await api.putMarginPlan(plan);
      setSavedPlans((current) => [
        ...current.filter((candidate) => candidate.id !== id),
        savedPlan,
      ].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedPlanId(id);
      storeSelectedMarginPlan(id);
      setPlanName(name);
      setSaveStatus(`${selectedPlanId || existingByName ? "Updated" : "Saved"} “${name}”.`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : "Could not save configuration.");
    } finally {
      setPlanMutationPending(false);
    }
  };
  const startNewPlan = () => {
    setSelectedPlanId("");
    storeSelectedMarginPlan("");
    setPlanName("");
    setSaveStatus("Name the current values to save them as a new configuration.");
  };
  const deleteSavedPlan = async () => {
    const plan = savedPlans.find((candidate) => candidate.id === selectedPlanId);
    if (!plan) return;
    setPlanMutationPending(true);
    try {
      await api.deleteMarginPlan(plan.id);
      setSavedPlans((current) => current.filter((candidate) => candidate.id !== plan.id));
      setSelectedPlanId("");
      storeSelectedMarginPlan("");
      setPlanName("");
      setSaveStatus(`Deleted “${plan.name}”. The current chart values were kept.`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : "Could not delete configuration.");
    } finally {
      setPlanMutationPending(false);
    }
  };
  const exportSelectedConfig = () => {
    const plan = savedPlans.find((candidate) => candidate.id === selectedPlanId);
    if (!plan) return;
    try {
      downloadMarginPlanConfig(buildMarginPlanConfigTransfer(plan.name, plan.inputs));
      setSaveStatus(`Downloaded “${plan.name}” for import into another environment.`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : "Could not export configuration.");
    }
  };
  const importConfig = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 1_000_000) {
      setSaveStatus("Configuration files must be smaller than 1 MB.");
      return;
    }
    setPlanMutationPending(true);
    try {
      const imported = parseMarginPlanConfigTransfer(await file.text(), DEFAULT_INPUTS);
      const existing = savedPlans.find(
        (plan) => plan.name.toLocaleLowerCase() === imported.name.toLocaleLowerCase(),
      );
      const plan: SavedMarginPlan = {
        id: existing?.id ?? crypto.randomUUID(),
        name: imported.name,
        inputs: imported.inputs,
        updatedAt: new Date().toISOString(),
      };
      const savedPlan = await api.putMarginPlan(plan);
      setSavedPlans((current) => [
        ...current.filter((candidate) => candidate.id !== savedPlan.id),
        savedPlan,
      ].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedPlanId(savedPlan.id);
      storeSelectedMarginPlan(savedPlan.id);
      setPlanName(savedPlan.name);
      setInputs(savedPlan.inputs);
      setPortfolioAssumptions(null);
      setMarginRateEstimate(null);
      setPortfolioStatus("");
      setMarginRateStatus("");
      setMaintenanceStatus("");
      setSaveStatus(`${existing ? "Updated" : "Imported"} “${savedPlan.name}”.`);
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : "Could not import configuration.");
    } finally {
      setPlanMutationPending(false);
    }
  };
  const createExport = () => {
    const toExportSummary = (summary: ReturnType<typeof fundingSummary>): FundingSummaryExport => ({
      finalPortfolio: summary.final.portfolioEnd,
      finalMargin: summary.final.marginEnd,
      finalNetWorth: summary.final.netWorth,
      totalInterest: summary.interest,
      totalAssetSales: summary.assetSales,
      totalDividendsForSpending: summary.dividendsForSpending,
      totalDividendTaxes: summary.dividendTaxes,
      totalRealizedCapitalGains: summary.capitalGains,
      totalCapitalGainsTaxes: summary.capitalGainsTaxes,
      totalUnfundedSpending: summary.unfundedSpending,
      minimumMaintenanceCushion: summary.minimumCushion,
    });
    return buildMarginPlanExport({
      scenarioName: planName,
      inputs,
      projection,
      simulation,
      marginFunding: toExportSummary(marginFunding),
      sellFunding: toExportSummary(sellFunding),
    });
  };
  const exportJson = () => {
    try {
      downloadMarginPlanJson(createExport());
      setExportStatus("Downloaded machine-readable JSON for independent analysis.");
    } catch (error) {
      setExportStatus(error instanceof Error ? error.message : "Could not export JSON.");
    }
  };
  const exportExcel = async () => {
    setExportingExcel(true);
    setExportStatus("");
    try {
      await downloadMarginPlanExcel(createExport());
      setExportStatus("Downloaded Excel workbook for professional review.");
    } catch (error) {
      setExportStatus(error instanceof Error ? error.message : "Could not export Excel workbook.");
    } finally {
      setExportingExcel(false);
    }
  };
  const applyLinkedPortfolio = async () => {
    if (!linked) return;
    setAnalyzingPortfolio(true);
    setPortfolioStatus("");
    setMarginRateStatus("");
    const marginablePortfolio = Math.max(
      0,
      linked.marginable_equity ?? linked.total_equity ?? linked.all_equity ?? 0,
    );
    const totalPortfolio = Math.max(
      marginablePortfolio,
      linked.all_equity ?? linked.total_equity ?? marginablePortfolio,
    );
    const retirementPortfolio = Math.max(0, totalPortfolio - marginablePortfolio);
    const allHoldings = linked.holdings ?? linked.marginable_holdings ?? [];
    const marginableHoldings = linked.marginable_holdings ?? linked.holdings ?? [];
    const maintenance = portfolioMaintenance(marginableHoldings, marginablePortfolio);
    const roundedMaintenancePercent = oneDecimal(maintenance.percent);
    const maxLtvPercent = 100 - roundedMaintenancePercent;
    setInputs((current) => ({
      ...current,
      startingPortfolio: marginablePortfolio,
      startingTaxableCostBasis: !current.capitalGainsTaxEnabled
        && Math.abs(current.startingTaxableCostBasis - current.startingPortfolio) < 0.01
        ? marginablePortfolio
        : current.startingTaxableCostBasis,
      retirementAssets: retirementPortfolio,
      initialMargin: Math.max(0, linked.margin_balance ?? 0),
      maxLtvPercent,
      pauseBorrowingLtvPercent: Math.min(
        current.pauseBorrowingLtvPercent,
        current.deleveragingLtvPercent,
        maxLtvPercent,
      ),
      deleveragingLtvPercent: Math.min(current.deleveragingLtvPercent, maxLtvPercent),
      targetLtvPercent: Math.min(
        current.targetLtvPercent,
        current.deleveragingLtvPercent,
        maxLtvPercent,
      ),
    }));
    setMaintenanceStatus(
      `Weighted maintenance set to ${roundedMaintenancePercent.toFixed(1)}% on `
      + `${money(marginablePortfolio, true)} of marginable assets: `
      + `${maintenance.counts.base} base, ${maintenance.counts.crypto} crypto, `
      + `${maintenance.counts.leveraged_etf} leveraged ETF holding${maintenance.counts.leveraged_etf === 1 ? "" : "s"}.`,
    );
    const positions = allHoldings
      .filter((holding) => holding.symbol && (holding.value ?? 0) > 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
      .slice(0, 12)
      .map((holding) => ({
        symbol: holding.symbol!,
        weight: totalPortfolio > 0
          ? ((holding.value ?? 0) / totalPortfolio) * 100
          : holding.weight_percent ?? 0,
      }));
    const [assumptionsResult, rateResult] = await Promise.allSettled([
      positions.length > 0 ? api.getPortfolioAssumptions(positions) : Promise.resolve(null),
      api.getMarginRateEstimate(),
    ]);

    if (assumptionsResult.status === "fulfilled" && assumptionsResult.value) {
      const assumptions = assumptionsResult.value;
      setPortfolioAssumptions(assumptions);
      if (assumptions.coverage_percent >= 60) {
        const useDividendEstimate = assumptions.dividend_yield_percent != null
          && assumptions.dividend_coverage_percent >= 60;
        setInputs((current) => ({
          ...current,
          expectedReturnPercent: oneDecimal(assumptions.expected_return_percent),
          volatilityPercent: oneDecimal(assumptions.volatility_percent),
          ...(useDividendEstimate
            ? { dividendYieldPercent: oneDecimal(assumptions.dividend_yield_percent!) }
            : {}),
        }));
        setPortfolioStatus(
          `${useDividendEstimate ? "Return, volatility, and dividend yield" : "Return and volatility"} updated from ${assumptions.positions.length} holding${assumptions.positions.length === 1 ? "" : "s"}.`
          + (useDividendEstimate ? "" : " Dividend coverage was too low, so the manual yield was kept."),
        );
      } else {
        setPortfolioStatus("Balances loaded, but historical coverage was too low to replace the return assumptions.");
      }
    } else if (positions.length === 0) {
      setPortfolioStatus("Balances loaded. No priced ticker holdings were available for historical assumptions.");
    } else {
      setPortfolioStatus("Balances loaded, but there was not enough market history to derive assumptions.");
    }

    if (rateResult.status === "fulfilled") {
      const estimate = rateResult.value;
      setMarginRateEstimate(estimate);
      if (estimate.estimated_rate_percent != null) {
        setInputs((current) => ({
          ...current,
          marginRatePercent: oneDecimal(estimate.estimated_rate_percent!),
        }));
        setMarginRateStatus(
          `Margin rate estimated from ${estimate.matched_expense_months} matched expense months (${estimate.confidence} confidence).`,
        );
      } else {
        setMarginRateStatus(estimate.reason ?? "Margin rate could not be estimated; the manual value was kept.");
      }
    } else {
      setMarginRateEstimate(null);
      setMarginRateStatus("Margin expenses could not be loaded; the manual rate was kept.");
    }
    setAnalyzingPortfolio(false);
  };

  return (
    <section className="space-y-6" data-testid="margin-planner">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Buy, borrow, die planner</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Model margin-funded retirement spending. Planning scenario only.
        </p>
      </div>

      <div
        className="sticky top-12 z-[5] -mx-2 rounded-lg border border-border bg-background/95 px-4 py-2.5 shadow-sm backdrop-blur sm:top-14 sm:mx-0"
        data-testid="sticky-resilience"
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="text-xs font-medium text-muted-foreground">Combined success</span>
            <span className="text-xl font-semibold tabular-nums">
              {simulation ? `${simulation.successPercent.toFixed(1)}%` : "Calculating…"}
            </span>
            {simulation && (
              <span className="hidden truncate text-xs text-muted-foreground md:inline">
                {simulation.successfulPaths.toLocaleString()} of {simulation.simulations.toLocaleString()} paths fund spending and avoid an LTV breach
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {simulationPending && simulation && (
              <span className="text-xs text-muted-foreground">Updating…</span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label="Save plan from header"
              disabled={plansLoading || planMutationPending}
              onClick={() => void savePlan()}
            >
              {planMutationPending ? "Saving…" : "Save"}
            </Button>
            <Badge variant={qualityVariant}>{quality}</Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>Saved configurations</CardTitle>
          <CardDescription>
            Select a plan to load it. Drafts stay local; named plans sync.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="saved-plan">Saved configuration</Label>
              <select
                id="saved-plan"
                value={selectedPlanId}
                onChange={(event) => chooseSavedPlan(event.target.value)}
                disabled={plansLoading || planMutationPending}
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <option value="">Select a configuration</option>
                {savedPlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
              </select>
              <Button className="w-full" type="button" variant="outline" disabled={!selectedPlanId || plansLoading || planMutationPending} onClick={() => void deleteSavedPlan()}>Delete selected configuration</Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-name">Configuration name</Label>
              <Input
                id="plan-name"
                value={planName}
                maxLength={60}
                placeholder="e.g. Retire at 45"
                onChange={(event) => {
                  setPlanName(event.target.value);
                  setSaveStatus("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void savePlan();
                }}
              />
              <div className="grid grid-cols-2 gap-2 [&>button]:w-full">
              <Button type="button" disabled={plansLoading || planMutationPending} onClick={() => void savePlan()}>{planMutationPending ? "Saving…" : selectedPlanId ? "Save changes" : "Save"}</Button>
              <Button type="button" variant="outline" disabled={plansLoading || planMutationPending} onClick={startNewPlan}>Save as new</Button>
              </div>
            </div>
          </div>
          <div className="mt-5 border-t border-border/70 pt-4">
            <p className="text-sm font-medium">Transfer configurations</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Move named settings between dev and production. Account data and simulation results are excluded.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:max-w-xl [&>button]:w-full">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!selectedPlanId || plansLoading || planMutationPending}
              onClick={exportSelectedConfig}
            >
              Export selected config
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={plansLoading || planMutationPending}
              onClick={() => document.getElementById("plan-config-import")?.click()}
            >
              Import config
            </Button>
            </div>
            <input
              id="plan-config-import"
              className="sr-only"
              type="file"
              accept="application/json,.json"
              aria-label="Choose planner configuration file"
              onChange={(event) => void importConfig(event)}
            />
          </div>
          {saveStatus && <p className="mt-3 text-xs text-muted-foreground" role="status">{saveStatus}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>Export for review</CardTitle>
          <CardDescription>
            Download the current assumptions, annual results, funding comparison, simulation summary, and model limitations. Brokerage account identifiers and holdings are excluded.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => void exportExcel()}
              disabled={simulationPending || exportingExcel}
            >
              {exportingExcel ? "Creating Excel…" : simulationPending ? "Waiting for simulation…" : "Export Excel"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={exportJson}
              disabled={simulationPending || exportingExcel}
            >
              Export JSON
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Excel is formatted for adviser review. JSON preserves field names and full precision for another analysis tool or LLM.
          </p>
          {exportStatus && <p className="mt-2 text-xs text-muted-foreground" role="status">{exportStatus}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Plan assumptions</CardTitle>
            <CardDescription>Editable nominal assumptions.</CardDescription>
            </div>
            {linked && (
              <Button type="button" variant="outline" size="sm" disabled={analyzingPortfolio} onClick={() => void applyLinkedPortfolio()}>
                {analyzingPortfolio ? "Analyzing portfolio…" : "Use linked portfolio"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h2 className="mb-1 text-sm font-semibold">Timeline</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Set the modeled age range.
            </p>
            <div className={CONTROL_GRID_CLASS} data-testid="timeline-control-grid">
              <NumberField id="current-age" label="Current age" value={inputs.currentAge} onChange={(value) => update("currentAge", value)} />
              <NumberField id="retirement-age" label="Stop working at" value={inputs.retirementAge} help="Retirement spending begins at the start of this age." onChange={(value) => {
                setInputs((current) => ({
                  ...current,
                  retirementAge: value,
                  coastIncomeEndAge: Math.min(
                    current.endAge,
                    Math.max(value, current.coastIncomeEndAge),
                  ),
                }));
                setSaveStatus("");
              }} />
              <NumberField id="retirement-access-age" label="Retirement account access age" value={inputs.retirementAccessAge} step={0.5} help="Age when retirement-account principal and dividends become available to fund spending. This is independent of when you stop working." onChange={(value) => update("retirementAccessAge", value)} />
              <NumberField id="end-age" label="Plan through age" value={inputs.endAge} onChange={(value) => {
                setInputs((current) => ({
                  ...current,
                  endAge: value,
                  coastIncomeEndAge: Math.max(
                    current.retirementAge,
                    Math.min(value, current.coastIncomeEndAge),
                  ),
                }));
                setSaveStatus("");
              }} />
            </div>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Starting position</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Opening balances before the first modeled month.
            </p>
            <div className={CONTROL_GRID_CLASS} data-testid="starting-position-control-grid">
              <NumberField id="starting-portfolio" label="Starting portfolio" value={inputs.startingPortfolio} step={1000} help="Invested assets available immediately. Enter retirement accounts and outstanding margin separately." onChange={(value) => {
                setInputs((current) => ({
                  ...current,
                  startingPortfolio: value,
                  startingTaxableCostBasis: !current.capitalGainsTaxEnabled
                    && Math.abs(current.startingTaxableCostBasis - current.startingPortfolio) < 0.01
                    ? value
                    : current.startingTaxableCostBasis,
                }));
                setSaveStatus("");
              }} />
              <NumberField id="initial-margin" label="Existing margin" value={inputs.initialMargin} step={1000} help="Margin debt outstanding before the first modeled contribution, return, or interest charge." onChange={(value) => update("initialMargin", value)} />
              <NumberField id="retirement-assets" label="Retirement accounts" value={inputs.retirementAssets} step={1000} min={0} help="Tax-advantaged retirement balances. They grow and count toward net worth immediately, but principal and dividends cannot fund spending until retirement age and never count as margin collateral." onChange={(value) => update("retirementAssets", value)} />
              <NumberField id="external-assets" label="External assets" value={inputs.externalAssets} step={1000} min={0} help="Assets outside the modeled investment portfolio, such as cash, real estate, or a private business. They add to net worth but not margin collateral or spending funds." onChange={(value) => update("externalAssets", value)} />
              <NumberField id="external-asset-growth" label="External asset growth" value={inputs.externalAssetGrowthPercent} step={0.1} min={0} suffix="%" help="Nominal annual growth applied to external assets. External assets are not sold or pledged in this model." onChange={(value) => update("externalAssetGrowthPercent", value)} />
            </div>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Working cash flow</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Enter contributions or derive them from income minus expenses.
            </p>
            <div className={CONTROL_GRID_CLASS} data-testid="working-cash-flow-control-grid">
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="cash-flow-mode" help="Manual mode uses the contribution fields. Income and expenses mode invests the positive monthly difference and treats a shortfall as spending.">Cash flow basis</FieldLabel>
                <select
                  id="cash-flow-mode"
                  value={inputs.cashFlowMode}
                  onChange={(event) => update(
                    "cashFlowMode",
                    event.target.value === "income_expenses"
                      ? "income_expenses"
                      : "manual_contributions",
                  )}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <option value="manual_contributions">Manual contributions</option>
                  <option value="income_expenses">Income minus expenses</option>
                </select>
              </div>
              {inputs.cashFlowMode === "income_expenses" ? (
                <>
                  <NumberField id="annual-working-income" label="Annual after-tax income" value={inputs.annualWorkingIncome} step={1000} onChange={(value) => update("annualWorkingIncome", value)} />
                  <NumberField id="income-growth" label="Income growth" value={inputs.incomeGrowthPercent} step={0.1} min={-10} suffix="%" onChange={(value) => update("incomeGrowthPercent", value)} />
                  <NumberField id="annual-working-expenses" label="Annual living expenses" value={inputs.annualWorkingExpenses} step={1000} onChange={(value) => update("annualWorkingExpenses", value)} />
                  <NumberField id="working-expense-inflation" label="Expense inflation" value={inputs.workingExpenseInflationPercent} step={0.1} min={-10} suffix="%" onChange={(value) => update("workingExpenseInflationPercent", value)} />
                </>
              ) : (
                <>
                  <div className="min-w-0 space-y-1.5">
                    <FieldLabel htmlFor="contribution-mode" help="Choose a fixed monthly deposit or an annual percentage of each year's opening portfolio, split into 12 deposits.">Contribution basis</FieldLabel>
                    <select
                      id="contribution-mode"
                      value={inputs.contributionMode}
                      onChange={(event) => update(
                        "contributionMode",
                        event.target.value === "portfolio_percent" ? "portfolio_percent" : "fixed",
                      )}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <option value="fixed">Fixed monthly dollars</option>
                      <option value="portfolio_percent">Annual portfolio percentage</option>
                    </select>
                  </div>
                  {inputs.contributionMode === "portfolio_percent" ? (
                    <NumberField
                      id="contribution-percent"
                      label="Annual portfolio contribution"
                      value={inputs.contributionPercent}
                      step={0.1}
                      max={100}
                      suffix="%"
                      onChange={(value) => update("contributionPercent", Math.min(100, Math.max(0, value)))}
                    />
                  ) : (
                    <NumberField id="monthly-contribution" label="Monthly cash contribution" value={inputs.monthlyContribution} step={100} onChange={(value) => update("monthlyContribution", value)} />
                  )}
                  <NumberField id="contribution-end-age" label="Stop contributions at" value={inputs.contributionEndAge} help="Deposits stop at the start of this age; this age itself receives no contributions." onChange={(value) => update("contributionEndAge", value)} />
                </>
              )}
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="contribution-strategy" help="Invest every deposit, or apply it to outstanding margin first and invest only the remainder.">Use contributions for</FieldLabel>
                <select
                  id="contribution-strategy"
                  value={inputs.contributionStrategy}
                  onChange={(event) => update(
                    "contributionStrategy",
                    event.target.value === "pay_margin_first" ? "pay_margin_first" : "invest",
                  )}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <option value="invest">Invest all</option>
                  <option value="pay_margin_first">Pay margin first</option>
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {inputs.cashFlowMode === "income_expenses"
                ? "Surplus is invested monthly. Shortfalls use the spending funding split."
                : `At the start, contributions equal ${startingContributionPercent.toFixed(2)}% of the portfolio per year.`}
              {" "}Pay margin first reduces debt before investing.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Retirement spending</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Set retirement spending, funding, and optional coast income.
            </p>
            <div className="space-y-3">
              <div className="rounded-md border border-border/70 bg-muted/10 p-3" data-testid="base-spending-group">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Base spending</h3>
                <div className={CONTROL_GRID_CLASS}>
                  <div className="min-w-0 space-y-1.5">
                    <FieldLabel htmlFor="spending-mode" help="Fixed dollars rise with spending inflation. Portfolio percentage recalculates from each year's opening portfolio and is not separately inflated.">Annual spending basis</FieldLabel>
                    <select
                      id="spending-mode"
                      value={inputs.spendingMode}
                      onChange={(event) => update(
                        "spendingMode",
                        event.target.value === "portfolio_percent" ? "portfolio_percent" : "fixed",
                      )}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <option value="fixed">Fixed dollars</option>
                      <option value="portfolio_percent">Portfolio percentage</option>
                    </select>
                  </div>
                  {inputs.spendingMode === "portfolio_percent" ? (
                    <NumberField
                      id="spending-percent"
                      label="Annual portfolio draw"
                      value={inputs.spendingPercent}
                      step={0.1}
                      max={100}
                      suffix="%"
                      help="Percentage of each year's opening portfolio allocated to that year's retirement spending."
                      onChange={(value) => update("spendingPercent", Math.min(100, Math.max(0, value)))}
                    />
                  ) : (
                    <NumberField
                      id="annual-spending"
                      label={inputs.spendingDollarBasis === "today"
                        ? "Annual spending today"
                        : "Annual spending at retirement"}
                      value={inputs.annualSpending}
                      step={1000}
                      help={inputs.spendingDollarBasis === "today"
                        ? "Today's spending is inflated through retirement and every year afterward."
                        : "Enter spending in first-retirement-year dollars; inflation begins after retirement."}
                      onChange={(value) => update("annualSpending", value)}
                    />
                  )}
                  {inputs.spendingMode === "fixed" && (
                    <div className="min-w-0 space-y-1.5">
                      <FieldLabel htmlFor="spending-dollar-basis" help="Choose whether the fixed amount is expressed in today's purchasing power or in dollars at the retirement date.">Fixed spending dollars</FieldLabel>
                      <select
                        id="spending-dollar-basis"
                        value={inputs.spendingDollarBasis}
                        onChange={(event) => update(
                          "spendingDollarBasis",
                          event.target.value === "today" ? "today" : "retirement_start",
                        )}
                        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      >
                        <option value="today">Today&apos;s dollars</option>
                        <option value="retirement_start">Retirement-start dollars</option>
                      </select>
                    </div>
                  )}
                  <NumberField id="inflation" label="Spending inflation" value={inputs.inflationPercent} step={0.1} min={-10} suffix="%" disabled={inputs.spendingMode === "portfolio_percent"} help={inputs.spendingDollarBasis === "today" ? "Applied from the current age onward." : "Applied after the first retirement year. It does not apply to portfolio-percentage spending."} onChange={(value) => update("inflationPercent", value)} />
                  <NumberField
                    id="spending-borrow-percent"
                    label="Spending funded by margin"
                    value={inputs.spendingBorrowPercent}
                    step={5}
                    max={100}
                    suffix="%"
                    help="Share of retirement spending added to margin debt. The remaining share is funded by selling portfolio assets."
                    onChange={(value) => update("spendingBorrowPercent", Math.min(100, Math.max(0, value)))}
                  />
                </div>
                {inputs.spendingMode === "fixed" && inputs.spendingDollarBasis === "today" && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="retirement-start-spending">
                    Estimated first-retirement-year spending: {money(retirementStartFixedSpending)}.
                  </p>
                )}
              </div>

              <div className="rounded-md border border-border/70 bg-muted/10 p-3" data-testid="dividend-group">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dividends</h3>
                <div className={CONTROL_GRID_CLASS}>
                  <NumberField
                    id="dividend-yield"
                    label="Annual dividend yield"
                    value={inputs.dividendYieldPercent}
                    step={0.1}
                    max={100}
                    suffix="%"
                    help="Cash yield modeled as part of total return, not additional return. Changes in yield are not modeled."
                    onChange={(value) => update("dividendYieldPercent", Math.min(100, Math.max(0, value)))}
                  />
                  <NumberField
                    id="dividend-tax-rate"
                    label="Effective taxable dividend tax"
                    value={inputs.dividendTaxRatePercent}
                    step={0.1}
                    max={100}
                    suffix="%"
                    help="Applied only to taxable-account dividends. The 23.8% default is a conservative high-income federal assumption: the 20% qualified-dividend rate plus the 3.8% Net Investment Income Tax. Adjust for your income, qualified-dividend share, filing status, and state taxes."
                    onChange={(value) => update("dividendTaxRatePercent", Math.min(100, Math.max(0, value)))}
                  />
                  <div className="min-w-0 space-y-1.5">
                    <FieldLabel htmlFor="dividend-use" help="Choose which use receives accessible, after-tax dividend cash first. The remaining cash follows the priority shown below.">Dividend cash priority</FieldLabel>
                    <select
                      id="dividend-use"
                      value={inputs.dividendUse}
                      onChange={(event) => update(
                        "dividendUse",
                        event.target.value === "pay_margin" ? "pay_margin" : "spending",
                      )}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <option value="spending">Spending first</option>
                      <option value="pay_margin">Margin first</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 rounded-md border border-border/70 bg-background/60 p-3" data-testid="dividend-priority-waterfall" aria-live="polite">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">Dividend cash flows in this order</p>
                  <ol className="grid gap-2 sm:grid-cols-3">
                    {(inputs.dividendUse === "pay_margin"
                      ? ["Pay down margin", "Fund spending", "Reinvest any remainder"]
                      : ["Fund spending", "Pay down margin", "Reinvest any remainder"]
                    ).map((step, index) => (
                      <li key={step} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${index === 0 ? "border-primary/60 bg-primary/10 font-medium" : "border-border/70"}`}>
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs tabular-nums">{index + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Federal qualified-dividend rates are 0%, 15%, or 20%; the 3.8% NIIT may also apply. Retirement-account dividends are not taxed when earned in this model. Verify your effective rate with a tax professional.
                </p>
                <div className="mt-4 border-t border-border/70 pt-4">
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Taxable asset sales</h4>
                  <div className={CONTROL_GRID_CLASS}>
                    <div className="min-w-0 space-y-1.5">
                      <FieldLabel htmlFor="capital-gains-tax" help="Estimate tax when taxable assets are sold for spending or automatic deleveraging. Retirement-account sales are excluded.">Capital-gains tax</FieldLabel>
                      <select
                        id="capital-gains-tax"
                        value={inputs.capitalGainsTaxEnabled ? "enabled" : "disabled"}
                        onChange={(event) => update("capitalGainsTaxEnabled", event.target.value === "enabled")}
                        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      >
                        <option value="disabled">Not modeled</option>
                        <option value="enabled">Estimate from basis</option>
                      </select>
                    </div>
                    <NumberField
                      id="taxable-cost-basis"
                      label="Starting taxable cost basis"
                      value={inputs.startingTaxableCostBasis}
                      step={1000}
                      disabled={!inputs.capitalGainsTaxEnabled}
                      help="Aggregate adjusted basis of the taxable portfolio. It may exceed current value when holdings have an unrealized loss."
                      onChange={(value) => update("startingTaxableCostBasis", Math.max(0, value))}
                    />
                    <NumberField
                      id="capital-gains-tax-rate"
                      label="Effective gains tax"
                      value={inputs.capitalGainsTaxRatePercent}
                      step={0.1}
                      max={100}
                      suffix="%"
                      disabled={!inputs.capitalGainsTaxEnabled}
                      help="Effective rate on realized gains. Include federal long-term capital-gains tax, applicable state tax, and NIIT when appropriate."
                      onChange={(value) => update("capitalGainsTaxRatePercent", Math.min(100, Math.max(0, value)))}
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Uses aggregate proportional basis. Contributions and reinvested after-tax dividends add basis; market growth does not. Tax-lot selection, holding periods, and capital-loss benefits are not modeled.
                  </p>
                </div>
              </div>

              <div className="rounded-md border border-border/70 bg-muted/10 p-3" data-testid="flexible-spending-group">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Down-market flexibility</h3>
                <div className={CONTROL_GRID_CLASS}>
                  <div className="min-w-0 space-y-1.5">
                    <FieldLabel htmlFor="flexible-spending" help="At the start of a retirement year, reduce spending only when both the market-drawdown and net-withdrawal-rate thresholds are met.">Down-market spending</FieldLabel>
                    <select
                      id="flexible-spending"
                      value={inputs.flexibleSpendingEnabled ? "flexible" : "fixed"}
                      onChange={(event) => update(
                        "flexibleSpendingEnabled",
                        event.target.value === "flexible",
                      )}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <option value="fixed">Keep planned spending</option>
                      <option value="flexible">Reduce in drawdowns</option>
                    </select>
                  </div>
                  <NumberField id="down-market-trigger" label="Cut at market drawdown" value={inputs.downMarketTriggerPercent} step={1} max={100} suffix="%" disabled={!inputs.flexibleSpendingEnabled} help="A spending cut is considered at the start of a retirement year when the modeled market is this far below its prior high." onChange={(value) => update("downMarketTriggerPercent", Math.min(100, Math.max(0, value)))} />
                  <NumberField id="spending-cut-withdrawal-rate" label="And withdrawal rate reaches" value={inputs.spendingCutWithdrawalRatePercent} step={0.1} max={100} suffix="%" disabled={!inputs.flexibleSpendingEnabled} help="The cut occurs only when the pre-cut net spending need divided by investments currently accessible for spending reaches this rate. Income is deducted before calculating the rate." onChange={(value) => update("spendingCutWithdrawalRatePercent", Math.min(100, Math.max(0, value)))} />
                  <NumberField id="down-market-spending-reduction" label="Annual spending cut" value={inputs.downMarketSpendingReductionPercent} step={1} max={100} suffix="%" disabled={!inputs.flexibleSpendingEnabled} help="Reduction from the prior year's spending for each retirement year that remains beyond the drawdown trigger." onChange={(value) => update("downMarketSpendingReductionPercent", Math.min(100, Math.max(0, value)))} />
                  <NumberField id="minimum-spending" label="Minimum planned spending" value={inputs.minimumSpendingPercent} step={5} max={100} suffix="%" disabled={!inputs.flexibleSpendingEnabled} help="Spending will not fall below this percentage of the normally planned amount." onChange={(value) => update("minimumSpendingPercent", Math.min(100, Math.max(0, value)))} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Both thresholds must be met. A drawdown alone will not reduce spending when the portfolio can comfortably support the net withdrawal.
                </p>
              </div>

              <div className="rounded-md border border-border/70 bg-muted/10 p-3" data-testid="retirement-income-group">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Retirement income</h3>
                <p className="mb-2 text-xs font-medium text-foreground">Coast income</p>
                <div className={CONTROL_GRID_CLASS}>
                  <div className="min-w-0 space-y-1.5">
                    <FieldLabel htmlFor="retirement-income-mode" help="Coast income is after-tax earned income received after retirement. It offsets living expenses before portfolio withdrawals or borrowing.">Income after retirement</FieldLabel>
                    <select
                      id="retirement-income-mode"
                      value={inputs.retirementIncomeMode}
                      onChange={(event) => update(
                        "retirementIncomeMode",
                        event.target.value === "coast" ? "coast" : "none",
                      )}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <option value="none">No earned income</option>
                      <option value="coast">Reduced coast income</option>
                    </select>
                  </div>
                  <NumberField id="annual-coast-income" label="Annual coast income" value={inputs.annualCoastIncome} step={1000} disabled={inputs.retirementIncomeMode !== "coast"} onChange={(value) => update("annualCoastIncome", value)} />
                  <NumberField id="coast-income-growth" label="Coast income growth" value={inputs.coastIncomeGrowthPercent} step={0.1} min={-10} suffix="%" disabled={inputs.retirementIncomeMode !== "coast"} onChange={(value) => update("coastIncomeGrowthPercent", value)} />
                  <NumberField id="coast-income-end-age" label="Coast income ends at" value={inputs.coastIncomeEndAge} min={inputs.retirementAge} max={inputs.endAge} disabled={inputs.retirementIncomeMode !== "coast"} help="Income is received until, but not including, this age." onChange={(value) => update("coastIncomeEndAge", value)} />
                </div>
                <p className="mb-2 mt-4 text-xs font-medium text-foreground">Social Security</p>
                <div className={CONTROL_GRID_CLASS}>
                  <NumberField id="social-security-benefit" label="Estimated monthly benefit" value={inputs.monthlySocialSecurityBenefit} step={50} help="Enter the monthly benefit shown by SSA in today's dollars. The planner applies the configured COLA from your current age and starts payments at the claim age. Taxes are excluded." onChange={(value) => update("monthlySocialSecurityBenefit", value)} />
                  <NumberField id="social-security-start-age" label="Claim age" value={inputs.socialSecurityStartAge} min={62} max={70} help="Social Security retirement benefits generally begin between ages 62 and 70. Use the age associated with your SSA estimate." onChange={(value) => update("socialSecurityStartAge", Math.min(70, Math.max(62, value)))} />
                  <NumberField id="social-security-cola" label="Social Security COLA" value={inputs.socialSecurityColaPercent} step={0.1} suffix="%" help="Assumed annual cost-of-living adjustment applied to the current-dollar estimate and subsequent payments." onChange={(value) => update("socialSecurityColaPercent", value)} />
                  <div className="flex items-end pb-2 text-xs text-muted-foreground">
                    <a className="underline hover:text-foreground" href="https://www.ssa.gov/prepare/get-benefits-estimate" target="_blank" rel="noreferrer">Get a personalized estimate from SSA</a>
                  </div>
                </div>
                {inputs.monthlySocialSecurityBenefit > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="social-security-payment-estimate">
                    Estimated first monthly payment at age {inputs.socialSecurityStartAge}: {money(estimatedSocialSecurityAtClaim)}. This projects your current-dollar SSA estimate using the selected COLA.
                  </p>
                )}
                <p className="mb-2 mt-4 text-xs font-medium text-foreground">Pension</p>
                <div className={CONTROL_GRID_CLASS}>
                  <NumberField id="pension-benefit" label="Monthly pension at start" value={inputs.monthlyPensionBenefit} step={50} help="Expected monthly pension when payments begin. Taxes and survivor-option reductions are not modeled." onChange={(value) => update("monthlyPensionBenefit", value)} />
                  <NumberField id="pension-start-age" label="Pension start age" value={inputs.pensionStartAge} help="Age when pension payments begin." onChange={(value) => update("pensionStartAge", value)} />
                  <NumberField id="pension-cola" label="Pension COLA" value={inputs.pensionColaPercent} step={0.1} suffix="%" help="Annual increase after pension payments begin. Use zero for a level pension." onChange={(value) => update("pensionColaPercent", value)} />
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Fixed spending inflates annually. Percentage spending resets yearly. Flexible cuts require both market stress and an elevated net withdrawal rate. Coast income offsets spending.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Market assumptions</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Return drives all paths. Volatility drives simulations.
            </p>
            <div className={CONTROL_GRID_CLASS} data-testid="market-assumptions-control-grid">
              <NumberField id="expected-return" label="Expected return" value={inputs.expectedReturnPercent} step={0.1} min={-20} suffix="%" help="Nominal compound annual growth rate applied to the invested portfolio. The baseline holds it constant; simulations vary around the same long-run compound rate." onChange={(value) => update("expectedReturnPercent", value)} />
              <NumberField id="volatility" label="Annual volatility" value={inputs.volatilityPercent} step={0.1} suffix="%" help="Annualized return variability used only for simulations. The linked estimate is a weighted average of standalone holding volatility and does not model diversification covariance." onChange={(value) => update("volatilityPercent", value)} />
            </div>
            {(portfolioStatus || portfolioAssumptions) && (
              <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="portfolio-assumption-status">
                <p>{portfolioStatus}</p>
                {portfolioAssumptions && (
                  <>
                    <p className="mt-1">
                      {portfolioAssumptions.coverage_percent.toFixed(1)}% of submitted portfolio weight had sufficient history. {portfolioAssumptions.method}. Volatility is conservatively blended without a diversification adjustment.
                    </p>
                    <p className="mt-1">
                      Tiingo trailing distribution yield: {portfolioAssumptions.dividend_yield_percent == null ? "unavailable" : `${portfolioAssumptions.dividend_yield_percent.toFixed(2)}%`} with {portfolioAssumptions.dividend_coverage_percent.toFixed(1)}% portfolio-weight coverage.
                    </p>
                    <div className="mt-2 text-foreground">
                      <p className="font-medium">Analyzed full allocation</p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid="analyzed-portfolio-allocation">
                        {portfolioAssumptions.positions.map((position) => (
                          <span key={position.symbol} className="tabular-nums">
                            {position.symbol} {position.weight.toFixed(1)}%
                            {position.dividend_yield_percent == null ? "" : ` · ${position.dividend_yield_percent.toFixed(2)}% yield`}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Baseline returns are steady. Simulations vary monthly returns using volatility.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Borrowing costs</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Set the baseline rate and simulation range.
            </p>
            <div
              className={CONTROL_GRID_CLASS}
              data-testid="borrowing-cost-grid"
            >
              <NumberField
                id="margin-rate"
                label={marginRateEstimate?.estimated_rate_percent != null ? "Baseline margin rate (estimated)" : "Baseline margin rate"}
                value={inputs.marginRatePercent}
                step={0.1}
                suffix="%"
                help="Annual borrowing rate used by the baseline projection. Fixed-rate simulations use this same rate."
                onChange={(value) => {
                  setMarginRateEstimate(null);
                  setMarginRateStatus("");
                  update("marginRatePercent", value);
                }}
              />
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="margin-rate-mode" help="Keep the baseline rate in every simulated year, or sample one annual rate per year from a range.">Simulation margin rates</FieldLabel>
                <select
                  id="margin-rate-mode"
                  value={inputs.marginRateMode}
                  onChange={(event) => update(
                    "marginRateMode",
                    event.target.value === "range" ? "range" : "fixed",
                  )}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <option value="fixed">Use baseline rate</option>
                  <option value="range">Variable range</option>
                </select>
              </div>
              {inputs.marginRateMode === "range" && (
                <>
                  <NumberField
                    id="margin-rate-min"
                    label="Minimum simulation rate"
                    value={inputs.marginRateMinPercent}
                    step={0.1}
                    suffix="%"
                    help="Lowest annual margin rate that variable-rate simulations may sample."
                    onChange={(value) => update("marginRateMinPercent", value)}
                  />
                  <NumberField
                    id="margin-rate-max"
                    label="Maximum simulation rate"
                    value={inputs.marginRateMaxPercent}
                    step={0.1}
                    suffix="%"
                    help="Highest annual margin rate that variable-rate simulations may sample."
                    onChange={(value) => update("marginRateMaxPercent", value)}
                  />
                </>
              )}
            </div>
            {marginRateStatus && (
              <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="margin-rate-status">
                <p>{marginRateStatus}</p>
                {marginRateEstimate?.estimated_rate_percent != null && (
                  <p className="mt-1 text-foreground">
                    Effective annual rate: {marginRateEstimate.estimated_rate_percent.toFixed(2)}%. You can edit it above.
                  </p>
                )}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Interest accrues monthly. Variable simulations sample one rate per year.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-1 text-sm font-semibold">Broker requirement</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Estimate the minimum equity your broker requires you to maintain.
            </p>
            <div className={CONTROL_GRID_CLASS} data-testid="broker-requirement-control-grid">
              <NumberField
                id="maintenance-requirement"
                label="Maintenance requirement"
                value={maintenanceRequirementPercent}
                step={0.1}
                max={100}
                suffix="%"
                help="Minimum equity your broker requires as a percentage of portfolio market value. The implied maximum LTV is 100% minus this value."
                onChange={(value) => {
                  setMaintenanceStatus("");
                  const maxLtvPercent = 100 - Math.min(100, Math.max(0, value));
                  setInputs((current) => ({
                    ...current,
                    maxLtvPercent,
                    pauseBorrowingLtvPercent: Math.min(
                      current.pauseBorrowingLtvPercent,
                      current.deleveragingLtvPercent,
                      maxLtvPercent,
                    ),
                    deleveragingLtvPercent: Math.min(current.deleveragingLtvPercent, maxLtvPercent),
                    targetLtvPercent: Math.min(
                      current.targetLtvPercent,
                      current.deleveragingLtvPercent,
                      maxLtvPercent,
                    ),
                  }));
                  setSaveStatus("");
                }}
              />
            </div>
            {maintenanceStatus && (
              <div
                className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground"
                data-testid="maintenance-status"
              >
                {maintenanceStatus}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Defaults are 25% unleveraged, 60% crypto, and 75% leveraged ETFs. Verify with your broker.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <h2 className="mb-3 text-sm font-semibold">Borrowing guardrails</h2>
            <div className={CONTROL_GRID_CLASS} data-testid="borrowing-guardrails-control-grid">
              <NumberField
                id="pause-borrowing-ltv"
                label="Pause new borrowing at"
                value={inputs.pauseBorrowingLtvPercent}
                step={1}
                max={100}
                suffix="%"
                help="At this loan-to-value ratio, the plan stops adding new spending to margin and funds spending from asset sales instead."
                onChange={(value) => update(
                  "pauseBorrowingLtvPercent",
                  Math.min(100, Math.max(0, value)),
                )}
              />
              <NumberField
                id="deleveraging-ltv"
                label="Deleverage at"
                value={inputs.deleveragingLtvPercent}
                step={1}
                max={100}
                suffix="%"
                help="At this loan-to-value ratio, the model sells assets to repay margin debt."
                onChange={(value) => update(
                  "deleveragingLtvPercent",
                  Math.min(100, Math.max(0, value)),
                )}
              />
              <NumberField
                id="target-ltv"
                label="Deleverage back to"
                value={inputs.targetLtvPercent}
                step={1}
                max={100}
                suffix="%"
                help="Loan-to-value target after a deleveraging sale. The model repays enough debt to return to this level."
                onChange={(value) => update(
                  "targetLtvPercent",
                  Math.min(100, Math.max(0, value)),
                )}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Pause switches spending to sales. Deleveraging sells assets until target LTV.
            </p>
            {(inputs.deleveragingLtvPercent > inputs.maxLtvPercent
              || inputs.pauseBorrowingLtvPercent > Math.min(inputs.deleveragingLtvPercent, inputs.maxLtvPercent)
              || inputs.targetLtvPercent > Math.min(inputs.deleveragingLtvPercent, inputs.maxLtvPercent)) && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400" role="status">
                One or more guardrails exceed an upstream limit. Entered values are preserved, but the simulation caps them at the maintenance or deleveraging threshold.
              </p>
            )}
            <div className={`${CONTROL_GRID_CLASS} mt-4`} data-testid="broker-stress-controls">
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="maintenance-shock" help="Stress-test an immediate broker increase in maintenance requirements at a chosen age.">Broker policy stress</FieldLabel>
                <select
                  id="maintenance-shock"
                  value={inputs.maintenanceShockEnabled ? "enabled" : "disabled"}
                  onChange={(event) => update("maintenanceShockEnabled", event.target.value === "enabled")}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <option value="disabled">Disabled</option>
                  <option value="enabled">Enabled</option>
                </select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="maintenance-shock-timing" help="Use one known start age, or sample one start age per Monte Carlo path from a range.">Shock timing</FieldLabel>
                <select
                  id="maintenance-shock-timing"
                  value={inputs.maintenanceShockTimingMode}
                  disabled={!inputs.maintenanceShockEnabled}
                  onChange={(event) => update(
                    "maintenanceShockTimingMode",
                    event.target.value as MarginPlanInputs["maintenanceShockTimingMode"],
                  )}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="fixed">Fixed age</option>
                  <option value="random_range">Random age range</option>
                </select>
              </div>
              <NumberField
                id="maintenance-shock-age"
                label={inputs.maintenanceShockTimingMode === "random_range" ? "Earliest shock age" : "Shock begins at age"}
                value={inputs.maintenanceShockAge}
                step={1}
                min={inputs.currentAge}
                max={inputs.endAge}
                disabled={!inputs.maintenanceShockEnabled}
                help={inputs.maintenanceShockTimingMode === "random_range"
                  ? "The earliest age at which a simulation may apply the shock."
                  : "The lower maximum LTV takes effect immediately at this age in every modeled path."}
                onChange={(value) => {
                  const age = Math.min(inputs.endAge, Math.max(inputs.currentAge, value));
                  update("maintenanceShockAge", age);
                  if (inputs.maintenanceShockLatestAge < age) update("maintenanceShockLatestAge", age);
                }}
              />
              {inputs.maintenanceShockTimingMode === "random_range" && (
                <NumberField
                  id="maintenance-shock-latest-age"
                  label="Latest shock age"
                  value={inputs.maintenanceShockLatestAge}
                  step={1}
                  min={inputs.maintenanceShockAge}
                  max={inputs.endAge}
                  disabled={!inputs.maintenanceShockEnabled}
                  help="Each simulation samples one whole-year start age through this age, inclusive."
                  onChange={(value) => update(
                    "maintenanceShockLatestAge",
                    Math.min(inputs.endAge, Math.max(inputs.maintenanceShockAge, value)),
                  )}
                />
              )}
              <div className="min-w-0 space-y-1.5">
                <FieldLabel htmlFor="maintenance-shock-duration" help="A temporary shock restores the normal maintenance limit afterward. Deleveraging already completed is not reversed.">Shock duration</FieldLabel>
                <select
                  id="maintenance-shock-duration"
                  value={inputs.maintenanceShockDurationYears}
                  disabled={!inputs.maintenanceShockEnabled}
                  onChange={(event) => update("maintenanceShockDurationYears", Number(event.target.value))}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value={1}>1 year</option>
                  <option value={3}>3 years</option>
                  <option value={5}>5 years</option>
                  <option value={10}>10 years</option>
                  <option value={0}>Rest of plan</option>
                </select>
              </div>
              <NumberField
                id="stressed-maintenance-requirement"
                label="Stressed maintenance requirement"
                value={100 - inputs.stressedMaxLtvPercent}
                step={1}
                min={maintenanceRequirementPercent}
                max={100}
                suffix="%"
                disabled={!inputs.maintenanceShockEnabled}
                help="The equity percentage the broker may require after the shock. A 100% requirement allows 0% LTV and requires all margin debt to be repaid."
                onChange={(value) => update(
                  "stressedMaxLtvPercent",
                  Math.max(0, Math.min(inputs.maxLtvPercent, 100 - value)),
                )}
              />
            </div>
            {inputs.maintenanceShockEnabled && (
              <p className="mt-2 text-xs text-muted-foreground">
                The fixed projection starts the shock at age {inputs.maintenanceShockAge}. Monte Carlo paths {inputs.maintenanceShockTimingMode === "random_range"
                  ? `sample a start age from ${inputs.maintenanceShockAge} through ${inputs.maintenanceShockLatestAge}`
                  : `also start it at age ${inputs.maintenanceShockAge}`}.
                {" "}It lasts {inputs.maintenanceShockDurationYears === 0 ? "for the rest of the plan" : `${inputs.maintenanceShockDurationYears} year${inputs.maintenanceShockDurationYears === 1 ? "" : "s"}`}.
                {" "}During the shock, {100 - inputs.stressedMaxLtvPercent}% maintenance permits at most {effectiveStressedMaxLtv.toFixed(1)}% LTV.
                {effectiveStressedMaxLtv === 0 && " Any outstanding margin must be repaid."}
              </p>
            )}
            {inputs.maintenanceShockEnabled && (
              <div
                className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                  stressedDeleveragingBuffer > 0
                    ? "border-success/40 bg-success/10 text-success"
                    : stressedDeleveragingBuffer < 0
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                }`}
                data-testid="broker-stress-buffer"
                role="status"
              >
                {stressedDeleveragingBuffer > 0 ? (
                  <p>
                    Stress buffer: the {effectiveStressedMaxLtv.toFixed(1)}% broker maximum is {stressedDeleveragingBuffer.toFixed(1)} percentage points above your {inputs.deleveragingLtvPercent.toFixed(1)}% intervention trigger.
                  </p>
                ) : stressedDeleveragingBuffer < 0 ? (
                  <p>
                    Broker stress overrides your guardrail: the {effectiveStressedMaxLtv.toFixed(1)}% maximum is {Math.abs(stressedDeleveragingBuffer).toFixed(1)} percentage points below your {inputs.deleveragingLtvPercent.toFixed(1)}% intervention trigger. At the shock, a path above the broker maximum records a breach before assets are sold.
                  </p>
                ) : (
                  <p>
                    No proactive stress buffer: the broker maximum and your intervention trigger are both {effectiveStressedMaxLtv.toFixed(1)}%. The next adverse move can cause a breach before assets are sold.
                  </p>
                )}
                {effectiveStressedTargetLtv >= effectiveStressedMaxLtv && (
                  <p className="mt-1">
                    The stressed sale target is capped at the broker maximum, leaving no post-sale buffer. Set “Deleverage back to” below {effectiveStressedMaxLtv.toFixed(1)}%.
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="spending-funding-comparison">
        <CardHeader>
          <CardTitle>Spending funding comparison</CardTitle>
          <CardDescription>
            Monte Carlo results use identical return and rate paths for both strategies. The detailed table below shows the fixed-return baseline.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {simulation && (
            <div className={`overflow-x-auto rounded-md border border-border ${simulationPending ? "opacity-60" : ""}`} data-testid="strategy-simulation-comparison">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Modeled-path metric</TableHead>
                    <TableHead className="text-right">Borrow remainder</TableHead>
                    <TableHead className="text-right">Sell + dividends</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Combined success", simulation.strategyComparison.margin.combinedSuccessPercent, simulation.strategyComparison.sellAssets.combinedSuccessPercent, "percent"],
                    ["Collateral survival", simulation.strategyComparison.margin.collateralSuccessPercent, simulation.strategyComparison.sellAssets.collateralSuccessPercent, "percent"],
                    ["Spending funded", simulation.strategyComparison.margin.spendingSuccessPercent, simulation.strategyComparison.sellAssets.spendingSuccessPercent, "percent"],
                    ["Median ending net worth", simulation.strategyComparison.margin.medianEndingNetWorth, simulation.strategyComparison.sellAssets.medianEndingNetWorth, "money"],
                    ["Median peak debt", simulation.strategyComparison.margin.medianPeakDebt, simulation.strategyComparison.sellAssets.medianPeakDebt, "money"],
                    ["Median lifetime interest", simulation.strategyComparison.margin.medianTotalInterest, simulation.strategyComparison.sellAssets.medianTotalInterest, "money"],
                    ["Median capital-gains taxes", simulation.strategyComparison.margin.medianCapitalGainsTaxes, simulation.strategyComparison.sellAssets.medianCapitalGainsTaxes, "money"],
                  ].map(([label, marginValue, sellValue, format]) => (
                    <TableRow key={label as string}>
                      <TableCell>{label}</TableCell>
                      <TableCell className="text-right tabular-nums">{format === "percent" ? `${(marginValue as number).toFixed(1)}%` : money(marginValue as number, true)}</TableCell>
                      <TableCell className="text-right tabular-nums">{format === "percent" ? `${(sellValue as number).toFixed(1)}%` : money(sellValue as number, true)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            <span className="text-muted-foreground">Sell + dividends ending net worth: </span>
            <span className="font-semibold tabular-nums">
              {deltaMoney(sellFunding.final.netWorth - marginFunding.final.netWorth)}
            </span>
            <span className="text-muted-foreground"> versus borrowing the remainder.</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead className="text-right">Borrow remainder</TableHead>
                  <TableHead className="text-right">Sell + dividends</TableHead>
                  <TableHead className="text-right">Difference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  ["Ending net worth", marginFunding.final.netWorth, sellFunding.final.netWorth],
                  ["Ending portfolio", marginFunding.final.portfolioEnd, sellFunding.final.portfolioEnd],
                  ["Ending margin debt", marginFunding.final.marginEnd, sellFunding.final.marginEnd],
                  ["Margin interest", marginFunding.interest, sellFunding.interest],
                  ["Assets sold", marginFunding.assetSales, sellFunding.assetSales],
                  ["Dividends used for spending", marginFunding.dividendsForSpending, sellFunding.dividendsForSpending],
                  ["Dividend taxes", marginFunding.dividendTaxes, sellFunding.dividendTaxes],
                  ["Realized capital gains", marginFunding.capitalGains, sellFunding.capitalGains],
                  ["Capital-gains taxes", marginFunding.capitalGainsTaxes, sellFunding.capitalGainsTaxes],
                  ["Minimum cushion", marginFunding.minimumCushion, sellFunding.minimumCushion],
                ].map(([label, marginValue, sellValue]) => (
                  <TableRow key={label as string}>
                    <TableCell>{label}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(marginValue as number, true)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(sellValue as number, true)}</TableCell>
                    <TableCell className="text-right tabular-nums">{deltaMoney((sellValue as number) - (marginValue as number))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            “Borrow remainder” uses your selected dividend priority. “Sell + dividends” applies after-tax dividends to spending, then sells assets for the shortfall. {inputs.capitalGainsTaxEnabled ? "Both strategies include the configured aggregate-basis capital-gains estimate." : "Capital-gains tax is currently disabled."} Retirement-withdrawal taxes remain excluded.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Baseline projection</CardTitle>
            <CardDescription>Fixed-return path.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <ProjectionChart rows={projection} inputs={inputs} />
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <div><p className="text-xs text-muted-foreground">Portfolio</p><p className="font-medium tabular-nums">{money(final.portfolioEnd, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Retirement accounts</p><p className="font-medium tabular-nums">{money(final.retirementAssetsEnd, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">External assets</p><p className="font-medium tabular-nums">{money(final.externalAssetsEnd, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Net worth</p><p className="font-medium tabular-nums">{money(final.netWorth, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Margin debt</p><p className="font-medium tabular-nums">{money(final.marginEnd, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Ending LTV</p><p className="font-medium tabular-nums">{Number.isFinite(final.ltvPercent) ? `${final.ltvPercent.toFixed(1)}%` : "Over limit"}</p></div>
              <div><p className="text-xs text-muted-foreground">Minimum cushion</p><p className="font-medium tabular-nums">{money(minimumCushion, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Total retirement spending</p><p className="font-medium tabular-nums">{money(totalRetirementExpenses, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Unfunded spending</p><p className="font-medium tabular-nums">{money(totalUnfundedSpending, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Total earned income</p><p className="font-medium tabular-nums">{money(totalEarnedIncome, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Social Security</p><p className="font-medium tabular-nums">{money(totalSocialSecurityIncome, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Pension income</p><p className="font-medium tabular-nums">{money(totalPensionIncome, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Total dividends</p><p className="font-medium tabular-nums">{money(totalDividendIncome, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Dividend taxes</p><p className="font-medium tabular-nums">{money(totalDividendTaxes, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Realized capital gains</p><p className="font-medium tabular-nums">{money(totalRealizedCapitalGains, true)}</p></div>
              <div><p className="text-xs text-muted-foreground">Capital-gains taxes</p><p className="font-medium tabular-nums">{money(totalCapitalGainsTaxes, true)}</p></div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {inputs.dividendUse === "spending" ? "Dividends to spending" : "Margin paid by dividends"}
                </p>
                <p className="font-medium tabular-nums">
                  {money(inputs.dividendUse === "spending" ? totalDividendSpending : totalDividendMarginRepayment, true)}
                </p>
              </div>
              <div><p className="text-xs text-muted-foreground">Total margin interest</p><p className="font-medium tabular-nums">{money(totalMarginInterest, true)}</p></div>
              {inputs.flexibleSpendingEnabled && (
                <div><p className="text-xs text-muted-foreground">Flexible spending cuts</p><p className="font-medium tabular-nums">{money(totalFlexibleSpendingReduction, true)}</p></div>
              )}
            </div>
            {totalDeleveragingSales > 0 && (
              <p className="text-xs text-muted-foreground">
                Baseline guardrails sell {money(totalDeleveragingSales, true)} of assets to repay margin debt.
              </p>
            )}
            {totalContributionMarginRepayment > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="contribution-margin-repayment">
                Baseline contributions repay {money(totalContributionMarginRepayment, true)} of margin debt before investing the remainder.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle>Modeled path results</CardTitle>
              <div className="flex items-center gap-2">
                {simulationPending && (
                  <span
                    className="text-xs text-muted-foreground"
                    role="status"
                    data-testid="simulation-status"
                  >
                    {simulation ? "Updating…" : "Calculating…"}
                  </span>
                )}
                <Badge variant={qualityVariant}>{quality}</Badge>
              </div>
            </div>
            <CardDescription>
              {(simulation?.simulations ?? DEFAULT_SIMULATION_COUNT).toLocaleString()} modeled paths · {simulationInputs.marginRateMode === "range" ? "variable" : "fixed"} rates · not historical odds.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {simulation ? (
              <>
                <div className={simulationPending ? "opacity-60" : undefined}>
                  <p className="text-3xl font-semibold tabular-nums">{simulation.successPercent.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground">
                    {simulation.successfulPaths.toLocaleString()} of {simulation.simulations.toLocaleString()} modeled paths fund planned spending and stay below {simulationInputs.maxLtvPercent.toFixed(0)}% LTV
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Guardrail deleveraging occurs in {simulation.deleveragingPaths.toLocaleString()} modeled paths.
                  </p>
                  {simulationInputs.flexibleSpendingEnabled && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="flexible-spending-paths">
                      Flexible spending activates in {simulation.flexibleSpendingPaths.toLocaleString()} modeled paths.
                    </p>
                  )}
                </div>
                <div className={`grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 ${simulationPending ? "opacity-60" : ""}`} data-testid="success-breakdown">
                  <div><p className="text-muted-foreground">Combined success</p><p className="font-medium tabular-nums">{simulation.successPercent.toFixed(1)}%</p></div>
                  <div><p className="text-muted-foreground">Collateral survival</p><p className="font-medium tabular-nums">{simulation.collateralSuccessPercent.toFixed(1)}%</p></div>
                  <div><p className="text-muted-foreground">Spending funded</p><p className="font-medium tabular-nums">{simulation.spendingSuccessPercent.toFixed(1)}%</p></div>
                  <div><p className="text-muted-foreground">Full budget funded</p><p className="font-medium tabular-nums">{simulation.fullSpendingPercent.toFixed(1)}%</p></div>
                </div>
                <div className={`grid grid-cols-3 gap-2 text-xs ${simulationPending ? "opacity-60" : ""}`}>
                  <div><p className="text-muted-foreground">10th ending</p><p className="font-medium tabular-nums">{money(simulation.terminalNetWorth.p10, true)}</p></div>
                  <div><p className="text-muted-foreground">Median ending</p><p className="font-medium tabular-nums">{money(simulation.terminalNetWorth.p50, true)}</p></div>
                  <div><p className="text-muted-foreground">90th ending</p><p className="font-medium tabular-nums">{money(simulation.terminalNetWorth.p90, true)}</p></div>
                </div>
                <div className={`rounded-md border border-border bg-muted/30 p-3 text-xs ${simulationPending ? "opacity-60" : ""}`} data-testid="failure-breakdown">
                  <p className="font-medium">First LTV breaches</p>
                  {simulation.failureBreakdown.failedPaths > 0 ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-muted-foreground">
                        {simulation.failureBreakdown.failedPaths.toLocaleString()} modeled paths cross the {simulationInputs.maxLtvPercent.toFixed(0)}% LTV limit at least once.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-muted-foreground">Down-market year</p>
                          <p className="font-medium tabular-nums">
                            {simulation.failureBreakdown.downMarketPaths.toLocaleString()} ({(simulation.failureBreakdown.downMarketPaths / simulation.failureBreakdown.failedPaths * 100).toFixed(1)}%)
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Debt outpaced portfolio</p>
                          <p className="font-medium tabular-nums">
                            {simulation.failureBreakdown.debtOutpacedPortfolioPaths.toLocaleString()} ({(simulation.failureBreakdown.debtOutpacedPortfolioPaths / simulation.failureBreakdown.failedPaths * 100).toFixed(1)}%)
                          </p>
                        </div>
                      </div>
                      <p className="text-muted-foreground">
                        First breach ages: earliest {simulation.failureBreakdown.earliestBreachAge}; median {simulation.failureBreakdown.medianBreachAge?.toFixed(0)}.
                      </p>
                      <p className="text-muted-foreground">
                        “Down-market” means portfolio growth was negative in the breach year; otherwise debt growth outpaced the portfolio.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-muted-foreground">None of the modeled paths cross the LTV limit.</p>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-md border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                Calculating resiliency without blocking the planner…
              </div>
            )}
            <div className="space-y-3">
              {factors.map((factor) => (
                <div key={factor.label} className="flex items-start gap-3">
                  <span className={`mt-1 size-2 shrink-0 rounded-full ${factor.tone === "positive" ? "bg-success" : factor.tone === "caution" ? "bg-warning" : "bg-destructive"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-3 text-sm"><span>{factor.label}</span><span className="shrink-0 font-medium tabular-nums">{factor.value}</span></div>
                    <p className="text-xs text-muted-foreground">{factor.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Projection milestones</CardTitle>
          <CardDescription>Annual through year 10 and retirement, then every five years.</CardDescription>
        </CardHeader>
        <CardContent className="[&>[data-slot=table-container]]:overflow-x-hidden md:[&>[data-slot=table-container]]:overflow-x-auto">
          <Table className="block md:table">
            <TableHeader className="hidden md:table-header-group">
              <TableRow>
                <TableHead>Age</TableHead>
                <TableHead className="text-right">Portfolio</TableHead>
                <TableHead className="text-right">Contrib / payoff</TableHead>
                <TableHead className="text-right">Income / expenses</TableHead>
                <TableHead className="text-right">Spend / sold</TableHead>
                <TableHead className="text-right">Flex cut</TableHead>
                <TableHead className="text-right">Interest</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead className="text-right">LTV</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="block divide-y divide-border md:table-row-group">
              {milestoneRows(projection, inputs.retirementAge).map((row) => (
                <TableRow key={row.year} data-testid={`projection-milestone-${row.year}`} className="grid grid-cols-2 gap-3 p-3 md:table-row md:p-0">
                  <TableCell className="col-span-2 block p-0 font-medium md:table-cell md:p-2">
                    <span className="md:hidden">Age </span>{row.age}
                  </TableCell>
                  <ProjectionMetricCell label="Portfolio">
                    {money(row.portfolioEnd, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Contrib / payoff">
                    {money(row.contribution, true)} / {money(row.marginRepaidFromContributions, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Income / expenses">
                    {money(row.earnedIncome, true)} / {money(row.livingExpenses, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Spend / sold">
                    {money(row.spendingTotal, true)} / {money(row.taxableAssetsSold + row.retirementAssetsSold, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Flex cut">
                    {money(row.spendingReduction, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Interest">
                    {money(row.marginInterest, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="Margin">
                    {money(row.marginEnd, true)}
                  </ProjectionMetricCell>
                  <ProjectionMetricCell label="LTV">
                    {Number.isFinite(row.ltvPercent) ? `${row.ltvPercent.toFixed(1)}%` : "—"}
                  </ProjectionMetricCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-warning/30 bg-warning/5">
        <CardContent className="space-y-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Important model limits</p>
          <p>
            Simulations use independent monthly lognormal total returns, a fixed dividend yield, annual rate regimes sampled inside your range, and monthly interest accrual. Dividend cash is carved out of total return to avoid double-counting. When enabled, capital-gains tax uses aggregate proportional basis and one effective rate; tax lots, holding periods, capital-loss benefits, changing tax brackets, and taxes on retirement withdrawals are not modeled. Fees, execution slippage, and yield changes are also excluded. The optional broker stress models one fixed or randomly timed maximum-LTV reduction for the selected duration, not repeated or security-specific policy changes. Historical averages do not guarantee future results. Brokers can raise requirements or liquidate securities without advance notice.
          </p>
          <p>
            Assumption references: <a className="underline hover:text-foreground" href="https://pages.stern.nyu.edu/adamodar/New_Home_Page/datafile/histretSPX.html" target="_blank" rel="noreferrer">historical U.S. stock returns</a>, the <a className="underline hover:text-foreground" href="https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins-29" target="_blank" rel="noreferrer">SEC margin risk bulletin</a>, <a className="underline hover:text-foreground" href="https://www.irs.gov/irb/2025-45_IRB" target="_blank" rel="noreferrer">2026 IRS qualified-dividend thresholds</a>, and the <a className="underline hover:text-foreground" href="https://www.irs.gov/taxtopics/tc559" target="_blank" rel="noreferrer">IRS NIIT guidance</a>.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
